import { API_URL, API_BASE, WS_URL } from '../config/api';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MediaImg from '../components/MediaImg';
import { 
  Users, Package, DollarSign, Clock, CheckCircle, XCircle, TrendingUp,
  Store, Crown, Search, Eye, Ban, Check, X, Settings, Truck, MapPin,
  BarChart3, CreditCard, ChevronRight, Menu, Home, UserCog, Cog, Sparkles, Star, MessageCircle,
  Trash2, Edit, Plus, AlertTriangle, RefreshCw, LogOut, Zap, Grip, Tag, Palette, Ruler, Footprints, Shirt, Gem, Weight, Box, Type, List, Hash, ChevronDown, ChevronUp, Image, Upload, GripVertical, Building2, Headphones
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../components/FloatingChat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import ImageUpload from '../components/ImageUpload';
import { toast } from 'sonner';
import AdminLiveTracking from '../components/AdminLiveTracking';

const API = API_URL;

const formatPrice = (price) => new Intl.NumberFormat('fr-FR').format(price);

const SIDEBAR_ITEMS = [
  { id: 'stats', label: 'Stats', icon: BarChart3, color: 'text-amber-400' },
  { id: 'users', label: 'Utilisateurs', icon: Users, color: 'text-rose-400' },
  { id: 'vendors', label: 'Vendeurs', icon: Store, color: 'text-purple-400' },
  { id: 'drivers', label: 'Livreurs', icon: Truck, color: 'text-blue-400' },
  { id: 'revendeurs', label: 'Revendeurs', icon: Package, color: 'text-indigo-400' },
  { id: 'enterprises', label: 'Entreprises', icon: Building2, color: 'text-green-400' },
  { id: 'products', label: 'Produits', icon: Package, color: 'text-green-400' },
  { id: 'messages', label: 'Messages', icon: MessageCircle, color: 'text-fuchsia-400' },
  { id: 'support', label: 'Support', icon: Headphones, color: 'text-purple-500' },
  { id: 'categories', label: 'Catégories', icon: Cog, color: 'text-teal-400' },
  { id: 'transactions', label: 'Transactions', icon: CreditCard, color: 'text-emerald-400' },
  { id: 'plans', label: 'Plans abonnement', icon: Crown, color: 'text-yellow-400' },
  { id: 'routes', label: 'Trajet livreurs', icon: MapPin, color: 'text-cyan-400' },
  { id: 'settings-vendors', label: 'Paramètres vendeurs', icon: UserCog, color: 'text-orange-400' },
  { id: 'settings-drivers', label: 'Paramètres livreurs', icon: Settings, color: 'text-pink-400' },
  { id: 'settings-general', label: 'Paramètre général', icon: Cog, color: 'text-slate-400' },
  { id: 'settings-layout', label: 'Apparence du site', icon: Cog, color: 'text-pink-400' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin, logout } = useAuth();
  const { openConversation } = useChat();
  
  const [activeSection, setActiveSection] = useState('stats');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [products, setProducts] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState({});
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [revendeurs, setRevendeurs] = useState([]);
  const [dropshippingStats, setDropshippingStats] = useState(null);
  const [dropshippingTransactions, setDropshippingTransactions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', banner_images: [], custom_fields: [] });
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [productFilter, setProductFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [adminConversations, setAdminConversations] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);

  // ===== AUTO-APPROVE STATES =====
  const [autoApprove, setAutoApprove] = useState({
    vendors: false,
    drivers: false,
    products: false,
    revendeurs: false,
  });
  
  const autoSweepRef = useRef(false);

  const loadAutoApproveFromPlatform = (platformSettings) => {
    setAutoApprove({
      vendors: Boolean(platformSettings?.auto_approve_vendors),
      drivers: Boolean(platformSettings?.auto_approve_drivers),
      products: Boolean(platformSettings?.auto_approve_products),
      revendeurs: Boolean(platformSettings?.auto_approve_revendeurs),
    });
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/connexion');
      return;
    }
    fetchAllData();
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin || !token) return;
    fetchAdminConversations();
    fetchSupportRequests();
  }, [isAdmin, token]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashRes, vendorsRes, driversRes, productsRes, pendingRes, transactionsRes, plansRes, revendeursRes, dropStatsRes, usersRes, catsRes, enterprisesRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/vendors`, { headers }),
        axios.get(`${API}/admin/drivers`, { headers }).catch(() => ({ data: { drivers: [] } })),
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/admin/products/pending`, { headers }),
        axios.get(`${API}/admin/transactions`, { headers }),
        axios.get(`${API}/subscriptions/plans`, { headers }),
        axios.get(`${API}/admin/revendeurs`, { headers }).catch(() => ({ data: { revendeurs: [] } })),
        axios.get(`${API}/admin/dropshipping/stats`, { headers }).catch(() => ({ data: { stats: {}, recent_transactions: [] } })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: { users: [] } })),
        axios.get(`${API}/categories`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/enterprises/admin/list`, { headers }).catch(() => ({ data: { enterprises: [] } }))
      ]);
      
      setStats(dashRes.data.stats);
      setVendors(vendorsRes.data.vendors || []);
      setDrivers(driversRes.data.drivers || []);
      setProducts(productsRes.data.products || []);
      setPendingProducts(pendingRes.data.products || []);
      setTransactions(transactionsRes.data.transactions || []);
      setSubscriptionPlans(plansRes.data || []);
      setRevendeurs(revendeursRes.data.revendeurs || []);
      setDropshippingStats(dropStatsRes.data.stats || {});
      setDropshippingTransactions(dropStatsRes.data.recent_transactions || []);
      setAllUsers(usersRes.data.users || []);
      setCategories(catsRes.data || []);
      setEnterprises(enterprisesRes.data.enterprises || []);
      
      const [vendorSettings, driverSettings, platformSettings] = await Promise.all([
        axios.get(`${API}/admin/settings/vendor`, { headers }),
        axios.get(`${API}/admin/settings/delivery`, { headers }),
        axios.get(`${API}/admin/settings/platform`, { headers })
      ]);
      
      const nextSettings = {
        vendor: vendorSettings.data,
        delivery: driverSettings.data,
        platform: platformSettings.data
      };
      setSettings(nextSettings);
      loadAutoApproveFromPlatform(nextSettings.platform || {});
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // ===== AUTO-APPROVE LOGIC =====
  const persistAutoApprove = async (nextAutoApprove) => {
    const headers = { Authorization: `Bearer ${token}` };
    const platform = settings.platform || {};
    const mergedPlatform = {
      ...platform,
      auto_approve_vendors: Boolean(nextAutoApprove.vendors),
      auto_approve_drivers: Boolean(nextAutoApprove.drivers),
      auto_approve_products: Boolean(nextAutoApprove.products),
      auto_approve_revendeurs: Boolean(nextAutoApprove.revendeurs),
    };

    await axios.put(
      `${API}/admin/settings/platform`,
      { settings: mergedPlatform },
      { headers }
    );

    setSettings((prev) => ({ ...prev, platform: mergedPlatform }));
  };

  const handleToggleAutoApprove = async (type) => {
    const newValue = !autoApprove[type];
    const nextAutoApprove = { ...autoApprove, [type]: newValue };
    setAutoApprove(nextAutoApprove);

    try {
      await persistAutoApprove(nextAutoApprove);
    } catch (error) {
      toast.error("Erreur sauvegarde du mode automatique");
      setAutoApprove(autoApprove);
      return;
    }

    if (newValue) {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        if (type === 'products') {
          await Promise.all(
            pendingProducts.map(p =>
              axios.post(`${API}/admin/products/${p.id}/approve`, {}, { headers })
            )
          );
          toast.success(`✅ ${pendingProducts.length} produit(s) approuvé(s) automatiquement`);
        } else if (type === 'vendors') {
          const pending = vendors.filter(v => !v.is_verified);
          await Promise.all(
            pending.map(v =>
              axios.put(`${API}/admin/vendors/${v.id}/verify`, {}, { headers })
            )
          );
          toast.success(`✅ ${pending.length} vendeur(s) approuvé(s) automatiquement`);
        } else if (type === 'drivers') {
          const pending = drivers.filter(d => !d.is_verified);
          await Promise.all(
            pending.map(d =>
              axios.put(`${API}/admin/drivers/${d.id}/verify`, {}, { headers })
            )
          );
          toast.success(`✅ ${pending.length} livreur(s) approuvé(s) automatiquement`);
        } else if (type === 'revendeurs') {
          const pending = revendeurs.filter(r => !r.is_active);
          await Promise.all(
            pending.map(r =>
              axios.put(`${API}/admin/revendeurs/${r.id}/verify`, {}, { headers })
            )
          );
          toast.success(`✅ ${pending.length} revendeur(s) approuvé(s) automatiquement`);
        }
        fetchAllData();
      } catch (error) {
        toast.error("Erreur lors de l'approbation automatique");
        const rollback = { ...nextAutoApprove, [type]: false };
        setAutoApprove(rollback);
        try {
          await persistAutoApprove(rollback);
        } catch {}
      }
    } else {
      toast.info(`Approbation automatique désactivée pour les ${type}`);
    }
  };

  useEffect(() => {
    if (!token || autoSweepRef.current) return;

    const shouldRun =
      (autoApprove.products && pendingProducts.length > 0) ||
      (autoApprove.vendors && vendors.some(v => !v.is_verified)) ||
      (autoApprove.drivers && drivers.some(d => !d.is_verified)) ||
      (autoApprove.revendeurs && revendeurs.some(r => !r.is_active));

    if (!shouldRun) return;

    const runAutoSweep = async () => {
      autoSweepRef.current = true;
      const headers = { Authorization: `Bearer ${token}` };
      try {
        if (autoApprove.products && pendingProducts.length > 0) {
          await Promise.all(
            pendingProducts.map(p => axios.post(`${API}/admin/products/${p.id}/approve`, {}, { headers }))
          );
        }
        if (autoApprove.vendors) {
          const pending = vendors.filter(v => !v.is_verified);
          await Promise.all(pending.map(v => axios.put(`${API}/admin/vendors/${v.id}/verify`, {}, { headers })));
        }
        if (autoApprove.drivers) {
          const pending = drivers.filter(d => !d.is_verified);
          await Promise.all(pending.map(d => axios.put(`${API}/admin/drivers/${d.id}/verify`, {}, { headers })));
        }
        if (autoApprove.revendeurs) {
          const pending = revendeurs.filter(r => !r.is_active);
          await Promise.all(pending.map(r => axios.put(`${API}/admin/revendeurs/${r.id}/verify`, {}, { headers })));
        }
        await fetchAllData();
      } catch (e) {
        console.error('Auto-sweep error:', e);
      } finally {
        autoSweepRef.current = false;
      }
    };

    runAutoSweep();
  }, [token, autoApprove, pendingProducts, vendors, drivers, revendeurs]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleApproveProduct = async (productId) => {
    try {
      await axios.post(`${API}/admin/products/${productId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produit approuvé !');
      fetchAllData();
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    }
  };

  const handleRejectProduct = async (productId) => {
    const reason = prompt('Raison du rejet :');
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/products/${productId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produit rejeté');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  const handleToggleVendorStatus = async (vendorId) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleVerifyVendor = async (vendorId) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendeur vérifié et activé !');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  const handleToggleProductFeatured = async (productId) => {
    try {
      const response = await axios.put(`${API}/admin/products/${productId}/feature`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const isFeaturedNow = response.data?.is_featured;
      setProducts(prev =>
        prev.map(p =>
          p.id === productId
            ? { ...p, is_featured: isFeaturedNow !== undefined ? isFeaturedNow : !p.is_featured }
            : p
        )
      );
      toast.success(response.data?.message || 'Mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise en avant');
    }
  };

  const handleVerifyDriver = async (driverId) => {
    try {
      await axios.put(`${API}/admin/drivers/${driverId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Livreur vérifié et activé !');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  const handleToggleDriver = async (driverId) => {
    try {
      await axios.put(`${API}/admin/drivers/${driverId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSaveSettings = async (type) => {
    try {
      await axios.put(`${API}/admin/settings/${type}`, { settings: settings[type] }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres sauvegardés !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const updateSetting = (type, key, value) => {
    setSettings(prev => ({
      ...prev,
      [type]: { ...prev[type], [key]: value }
    }));
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${userName}" ?\n\nTous ses produits et données seront supprimés définitivement.`)) return;
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleToggleUserActive = async (userId) => {
    try {
      const response = await axios.put(`${API}/admin/users/${userId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Supprimer le produit "${productName}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produit supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteVendor = async (vendorId, vendorName) => {
    if (!window.confirm(`Supprimer le vendeur "${vendorName}" ?\n\nTous ses produits et données seront supprimés définitivement.`)) return;
    try {
      const response = await axios.delete(`${API}/admin/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteDriver = async (driverId, driverName) => {
    if (!window.confirm(`Supprimer le livreur "${driverName}" ?\n\nToutes ses données seront supprimées définitivement.`)) return;
    try {
      await axios.delete(`${API}/admin/drivers/${driverId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Livreur supprimé');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteRevendeur = async (revendeurId, revendeurName) => {
    if (!window.confirm(`Supprimer le revendeur "${revendeurName}" ?\n\nTous ses produits et données seront supprimés définitivement.`)) return;
    try {
      const response = await axios.delete(`${API}/admin/revendeurs/${revendeurId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleAdminWriteMessage = async (targetUser) => {
    try {
      const targetUserId = targetUser?.id || targetUser?.user_id;
      if (!targetUserId) {
        toast.error("Utilisateur introuvable pour ouvrir le chat");
        return;
      }
      const response = await axios.post(
        `${API}/admin/conversations/start`,
        { target_user_id: targetUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const conversationId = response.data?.id;
      if (!conversationId) {
        toast.error("Conversation non créée");
        return;
      }
      openConversation(conversationId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ouverture du chat");
    }
  };

  const handleOpenConversation = (conversationId) => {
    openConversation(conversationId);
  };

  const fetchAdminConversations = async () => {
    try {
      const response = await axios.get(`${API}/admin/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminConversations(response.data?.conversations || []);
    } catch (error) {
      // Don't show error toast for 401 errors - admin might not be authenticated
      if (error.response?.status !== 401) {
        toast.error('Erreur de chargement des messages admin');
      }
    }
  };

  const fetchSupportRequests = async () => {
    try {
      const response = await axios.get(`${API}/support/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupportRequests(response.data?.requests || []);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Erreur de chargement des demandes de support');
      }
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name) { toast.error('Le nom est requis'); return; }
    try {
      const slug = newCategory.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      await axios.post(`${API}/admin/categories`, { ...newCategory, slug, icon: 'Package', custom_fields: newCategory.custom_fields || [] }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie créée');
      setNewCategory({ name: '', description: '', banner_images: [], custom_fields: [] });
      setShowNewCategoryForm(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    if (!editingCategory) return;
    try {
      const slug = (editingCategory.slug && editingCategory.slug.trim())
        ? editingCategory.slug
        : editingCategory.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      await axios.put(`${API}/admin/categories/${categoryId}`, { ...editingCategory, slug, icon: editingCategory.icon || 'Package', custom_fields: editingCategory.custom_fields || [] }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie mise à jour');
      setEditingCategory(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Supprimer la catégorie "${categoryName}" ?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie supprimée');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleToggleCategory = async (categoryId) => {
    try {
      const response = await axios.put(`${API}/admin/categories/${categoryId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const filteredUsers = allUsers.filter(u => {
    const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
    const matchesSearch = !userSearch ||
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase());
    return matchesRole && matchesSearch;
  });

  const { vendorsWithCounts, revendeursWithCounts } = useMemo(() => {
    const vendorCountById = new Map();
    const revendeurCountByShopSlug = new Map();
    const revendeurCountById = new Map();

    for (const p of products || []) {
      const sellerId = p?.seller_id;
      const shopSlug = p?.shop_slug;

      if (sellerId) {
        vendorCountById.set(sellerId, (vendorCountById.get(sellerId) || 0) + 1);
        revendeurCountById.set(sellerId, (revendeurCountById.get(sellerId) || 0) + 1);
      }

      if (shopSlug) {
        revendeurCountByShopSlug.set(shopSlug, (revendeurCountByShopSlug.get(shopSlug) || 0) + 1);
      }
    }

    return {
      vendorsWithCounts: vendors.map((v) => ({
        ...v,
        product_count: vendorCountById.get(v.id) || 0,
      })),
      revendeursWithCounts: revendeurs.map((r) => ({
        ...r,
        product_count: revendeurCountByShopSlug.get(r.shop_slug) || revendeurCountById.get(r.id) || 0,
      })),
    };
  }, [vendors, revendeurs, products]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex">
        <div className="w-64 bg-slate-800 p-4">
          {[...Array(9)].map((_, i) => <Skeleton key={i} className="h-10 mb-2 bg-slate-700" />)}
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="h-10 w-64 mb-8 bg-slate-800" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 bg-slate-800" />)}
          </div>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'stats':
        return <StatsSection stats={stats} pendingCount={pendingProducts.length} pendingVendors={vendors.filter(v => !v.is_verified).length} categories={categories} products={products} />;
      case 'users':
        return <UsersSection users={filteredUsers} roleFilter={userRoleFilter} setRoleFilter={setUserRoleFilter} search={userSearch} setSearch={setUserSearch} onDelete={handleDeleteUser} onToggleActive={handleToggleUserActive} />;
      case 'vendors':
        return <VendorsSection vendors={vendorsWithCounts} onToggle={handleToggleVendorStatus} onVerify={handleVerifyVendor} onDelete={handleDeleteVendor} onMessage={handleAdminWriteMessage} searchTerm={searchTerm} autoApprove={autoApprove.vendors} onToggleAutoApprove={() => handleToggleAutoApprove('vendors')} />;
      case 'drivers':
        return <DriversSection drivers={drivers} onVerify={handleVerifyDriver} onToggle={handleToggleDriver} onDelete={handleDeleteDriver} onMessage={handleAdminWriteMessage} autoApprove={autoApprove.drivers} onToggleAutoApprove={() => handleToggleAutoApprove('drivers')} />;
      case 'revendeurs':
        return <RevendeursSection revendeurs={revendeursWithCounts} stats={dropshippingStats} transactions={dropshippingTransactions} token={token} onRefresh={fetchAllData} onDelete={handleDeleteRevendeur} onMessage={handleAdminWriteMessage} autoApprove={autoApprove.revendeurs} onToggleAutoApprove={() => handleToggleAutoApprove('revendeurs')} />;
      case 'enterprises':
        return <EnterprisesSection enterprises={enterprises} onRefresh={fetchAllData} />;
      case 'products':
        return <ProductsSection products={products} pendingProducts={pendingProducts} filter={productFilter} setFilter={setProductFilter} onApprove={handleApproveProduct} onReject={handleRejectProduct} onToggleFeatured={handleToggleProductFeatured} onDelete={handleDeleteProduct} autoApprove={autoApprove.products} onToggleAutoApprove={() => handleToggleAutoApprove('products')} />;
      case 'messages':
        return <AdminMessagesSection conversations={adminConversations} onRefresh={fetchAdminConversations} onOpenConversation={handleOpenConversation} />;
      case 'support':
        return <SupportSection supportRequests={supportRequests} onRefresh={fetchSupportRequests} />;
      case 'categories':
        return <CategoriesSection token={token} categories={categories} editingCategory={editingCategory} setEditingCategory={setEditingCategory} newCategory={newCategory} setNewCategory={setNewCategory} showNewForm={showNewCategoryForm} setShowNewForm={setShowNewCategoryForm} onCreate={handleCreateCategory} onUpdate={handleUpdateCategory} onDelete={handleDeleteCategory} onToggle={handleToggleCategory} onRefresh={fetchAllData} products={products} />;
      case 'transactions':
        return <TransactionsSection transactions={transactions} />;
      case 'plans':
        return <PlansSection plans={subscriptionPlans} vendors={vendors} />;
      case 'routes':
        return <AdminLiveTracking token={token} />;
      case 'settings-vendors':
        return <SettingsSection type="vendor" settings={settings.vendor} onUpdate={(k, v) => updateSetting('vendor', k, v)} onSave={() => handleSaveSettings('vendor')} />;
      case 'settings-drivers':
        return <SettingsSection type="delivery" settings={settings.delivery} onUpdate={(k, v) => updateSetting('delivery', k, v)} onSave={() => handleSaveSettings('delivery')} />;
      case 'settings-general':
        return <SettingsSection type="platform" settings={settings.platform} onUpdate={(k, v) => updateSetting('platform', k, v)} onSave={() => handleSaveSettings('platform')} />;
      case 'settings-layout':
        return <LayoutAppearanceSection token={token} API={API} />;
      default:
        return <StatsSection stats={stats} pendingCount={pendingProducts.length} pendingVendors={vendors.filter(v => !v.is_verified).length} categories={categories} products={products} />;
    }
  };

  return (
    <div className="min-h-screen premium-dashboard-bg dashboard-card-skin text-white flex" data-testid="admin-dashboard">
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} premium-panel border-r border-slate-700 transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-500" />
              <span className="font-bold">Admin</span>
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-white">
            <Menu className="w-5 h-5" />
          </Button>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {SIDEBAR_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            const isPending = item.id === 'products' && pendingProducts.length > 0;
            return (
              <button key={item.id} onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive ? 'bg-slate-700 text-white' : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'}`}
                data-testid={`nav-${item.id}`}
              >
                <Icon className={`w-5 h-5 ${item.color}`} />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left text-sm">{item.label}</span>
                    {isPending && (
                      <span className="bg-amber-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                        {pendingProducts.length}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-700">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
            <Home className="w-4 h-4" />
            {sidebarOpen && <span>Voir la boutique</span>}
          </Link>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <header className="premium-panel border-b border-slate-700 p-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">{SIDEBAR_ITEMS.find(i => i.id === activeSection)?.label || 'Dashboard'}</h1>
              <p className="text-sm text-slate-400">Bienvenue, {user?.name}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 w-64 bg-slate-700 border-slate-600 text-white" />
              </div>
              <Button variant="destructive" onClick={handleLogout} className="flex items-center gap-2">
                <LogOut className="w-4 h-4" /> Déconnexion
              </Button>
            </div>
          </div>
        </header>
        <div className="p-6 space-y-4">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

// ===== AUTO-APPROVE TOGGLE =====
const AutoApproveToggle = ({ enabled, onToggle, label }) => (
  <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${enabled ? 'bg-green-500/10 border-green-500/40' : 'bg-slate-700/50 border-slate-600'} transition-all`}>
    <Zap className={`w-5 h-5 ${enabled ? 'text-green-400' : 'text-slate-400'}`} />
    <div className="flex-1">
      <p className={`text-sm font-semibold ${enabled ? 'text-green-300' : 'text-slate-300'}`}>
        Approbation automatique
      </p>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
    <button
      onClick={onToggle}
      className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-slate-600'}`}
    >
      <div className={`w-5 h-5 bg-white rounded-full transition-transform mx-0.5 ${enabled ? 'translate-x-6' : 'translate-x-0'}`} />
    </button>
  </div>
);

// ===== STATS SECTION avec produits par catégorie =====
const StatsSection = ({ stats, pendingCount, pendingVendors, categories, products }) => {
  // Calcul dynamique du nombre de produits par catégorie
  const productsByCategory = categories.map(cat => ({
    name: cat.name,
    count: products.filter(p => p.category_slug === cat.slug).length,
    slug: cat.slug,
  })).sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6">
      {pendingCount > 0 && (
        <div className="p-4 bg-amber-500/20 border border-amber-500/50 rounded-xl flex items-center gap-3">
          <Clock className="w-6 h-6 text-amber-400" />
          <div>
            <p className="font-bold text-amber-200">{pendingCount} produit(s) en attente de validation</p>
            <p className="text-sm text-amber-300/70">Des vendeurs attendent votre approbation</p>
          </div>
        </div>
      )}
      {pendingVendors > 0 && (
        <div className="p-4 bg-purple-500/20 border border-purple-500/50 rounded-xl flex items-center gap-3">
          <Store className="w-6 h-6 text-purple-400" />
          <div>
            <p className="font-bold text-purple-200">{pendingVendors} vendeur(s) en attente de vérification</p>
            <p className="text-sm text-purple-300/70">De nouveaux vendeurs attendent votre approbation</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} color="text-blue-400" value={stats?.total_users || 0} label="Utilisateurs" />
        <StatCard icon={Store} color="text-purple-400" value={stats?.total_vendors || 0} label="Vendeurs" />
        <StatCard icon={Truck} color="text-cyan-400" value={stats?.total_drivers || 0} label="Livreurs" />
        <StatCard icon={Package} color="text-green-400" value={stats?.total_products || 0} label="Produits" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Clock} color="text-amber-400" value={stats?.pending_products || 0} label="En attente" />
        <StatCard icon={CheckCircle} color="text-emerald-400" value={stats?.approved_products || 0} label="Approuvés" />
        <StatCard icon={Crown} color="text-yellow-400" value={stats?.paid_vendors || 0} label="Vendeurs payants" />
        <StatCard icon={DollarSign} color="text-green-400" value={`$${stats?.total_revenue_usd?.toFixed(2) || 0}`} label="Revenus" />
      </div>

      {/* ===== PRODUITS PAR CATÉGORIE ===== */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h3 className="font-bold mb-5 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-teal-400" /> Produits par catégorie
        </h3>
        {productsByCategory.length === 0 ? (
          <p className="text-slate-400 text-sm">Aucune donnée disponible</p>
        ) : (
          <div className="space-y-3">
            {productsByCategory.map((cat) => {
              const maxCount = productsByCategory[0]?.count || 1;
              const pct = maxCount > 0 ? (cat.count / maxCount) * 100 : 0;
              return (
                <div key={cat.slug} className="flex items-center gap-3">
                  <span className="text-sm text-slate-300 w-40 truncate">{cat.name}</span>
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-teal-400 w-8 text-right">{cat.count}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" /> Aperçu des ventes
          </h3>
          <div className="h-48 flex items-center justify-center text-slate-500">Graphique à venir</div>
        </div>
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-400" /> Répartition abonnements
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Gratuit</span>
              <span className="font-bold">{stats?.free_vendors || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Payants</span>
              <span className="font-bold text-green-400">{stats?.paid_vendors || 0}</span>
            </div>
            <div className="border-t border-slate-700 pt-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Taux conversion</span>
                <span className="font-bold text-amber-400">
                  {stats?.total_vendors > 0 ? ((stats?.paid_vendors / stats?.total_vendors) * 100).toFixed(1) : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, color, value, label }) => (
  <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
    <Icon className={`w-6 h-6 ${color} mb-2`} />
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-sm text-slate-400">{label}</p>
  </div>
);

const VendorsSection = ({ vendors, onToggle, onVerify, onDelete, onMessage, searchTerm, autoApprove, onToggleAutoApprove }) => {
  const filteredVendors = vendors.filter(v =>
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const sortedVendors = [...filteredVendors].sort((a, b) => {
    if (!a.is_verified && b.is_verified) return -1;
    if (a.is_verified && !b.is_verified) return 1;
    return 0;
  });

  return (
    <div className="space-y-4">
      <AutoApproveToggle
        enabled={autoApprove}
        onToggle={onToggleAutoApprove}
        label={`Active : vérifie automatiquement tous les vendeurs en attente (${sortedVendors.filter(v => !v.is_verified).length} en attente)`}
      />

      {sortedVendors.filter(v => !v.is_verified).length > 0 && (
        <div className="p-4 bg-purple-500/20 border border-purple-500/50 rounded-xl flex items-center gap-3">
          <Clock className="w-6 h-6 text-purple-400" />
          <div>
            <p className="font-bold text-purple-200">{sortedVendors.filter(v => !v.is_verified).length} vendeur(s) en attente de vérification</p>
            <p className="text-sm text-purple-300/70">Vérifiez leurs informations avant de les activer</p>
          </div>
        </div>
      )}

      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto touch-scroll-x">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Vendeur</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Abonnement</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Produits</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Vérification</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedVendors.map((vendor) => (
                <tr key={vendor.id} className={`border-t border-slate-700 hover:bg-slate-700/30 ${!vendor.is_verified ? 'bg-purple-500/5' : ''}`}>
                  <td className="p-4">
                    <p className="font-medium">{vendor.shop_name || vendor.name}</p>
                    <p className="text-sm text-slate-400">{vendor.email}</p>
                    {vendor.phone && <p className="text-xs text-slate-500">{vendor.phone}</p>}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      vendor.subscription_plan === 'free' ? 'bg-slate-600' :
                      vendor.subscription_plan === 'artisan' ? 'bg-blue-500/20 text-blue-400' :
                      vendor.subscription_plan === 'commercant' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-purple-500/20 text-purple-400'
                    }`}>
                      {vendor.subscription_plan || 'free'}
                    </span>
                  </td>
                  <td className="p-4 font-medium">{vendor.product_count || 0}</td>
                  <td className="p-4">
                    {vendor.is_verified ? (
                      <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle className="w-4 h-4" /> Vérifié</span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-400 text-xs"><Clock className="w-4 h-4" /> En attente</span>
                    )}
                  </td>
                  <td className="p-4">
                    {vendor.is_active ? (
                      <span className="flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="w-4 h-4" /> Actif</span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-400 text-sm"><XCircle className="w-4 h-4" /> Inactif</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <Button asChild size="sm" variant="outline" className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10">
                        <Link to={`/vendeur-boutique/${vendor.id}`} target="_blank" rel="noopener noreferrer">
                          <Eye className="w-4 h-4 mr-1" /> Voir la boutique
                        </Link>
                      </Button>
                      <Button size="sm" variant="outline" className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10" onClick={() => onMessage(vendor)}>
                        Écrire
                      </Button>
                      {!vendor.is_verified && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onVerify(vendor.id)}>
                          <Check className="w-4 h-4 mr-1" /> Vérifier
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onToggle(vendor.id)} className="text-slate-400 hover:text-white" title={vendor.is_active ? 'Désactiver' : 'Activer'}>
                        {vendor.is_active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(vendor.id, vendor.shop_name || vendor.name)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-vendor-${vendor.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sortedVendors.length === 0 && <div className="p-12 text-center text-slate-500">Aucun vendeur trouvé</div>}
      </div>
    </div>
  );
};

const DriversSection = ({ drivers, onVerify, onToggle, onDelete, onMessage, autoApprove, onToggleAutoApprove }) => (
  <div className="space-y-4">
    <AutoApproveToggle
      enabled={autoApprove}
      onToggle={onToggleAutoApprove}
      label={`Active : vérifie automatiquement tous les livreurs en attente (${drivers.filter(d => !d.is_verified).length} en attente)`}
    />

    {drivers.length === 0 ? (
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
        <Truck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">Aucun livreur inscrit</h3>
        <p className="text-slate-400">Les livreurs apparaîtront ici après leur inscription</p>
      </div>
    ) : (
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-700/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Livreur</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Véhicule</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Ville</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Permis</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((driver) => (
              <tr key={driver.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                <td className="p-4">
                  <p className="font-medium">{driver.name}</p>
                  <p className="text-sm text-slate-400">{driver.email}</p>
                  <p className="text-xs text-slate-500">{driver.phone}</p>
                </td>
                <td className="p-4 capitalize">{driver.vehicle_type}</td>
                <td className="p-4">{driver.city}</td>
                <td className="p-4">
                  {driver.license_image ? (
                    <a 
                      href={`${API_BASE}${driver.license_image}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded-lg text-sm transition-all"
                    >
                      <Image className="w-4 h-4" />
                      Voir le permis
                    </a>
                  ) : (
                    <span className="text-slate-600 text-sm">—</span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex flex-col gap-1">
                    {driver.is_verified ? (
                      <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Vérifié</span>
                    ) : (
                      <span className="text-amber-400 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</span>
                    )}
                    {driver.is_active ? <span className="text-green-400 text-xs">Actif</span> : <span className="text-red-400 text-xs">Inactif</span>}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex gap-2">
                    {!driver.is_verified && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onVerify(driver.id)}>
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => onToggle(driver.id)} className="text-slate-400" title={driver.is_active ? 'Désactiver' : 'Activer'}>
                      {driver.is_active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(driver.id, driver.name)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-driver-${driver.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10" onClick={() => onMessage(driver)}>
                      Écrire
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const EnterprisesSection = ({ enterprises, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredEnterprises = enterprises.filter(e => 
    e.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteEnterprise = async (enterpriseId, companyName) => {
    if (!window.confirm(`Supprimer l'entreprise "${companyName}" ?\n\nToutes ses données seront supprimées définitivement.`)) return;
    try {
      const API = API_URL;
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/enterprises/admin/${enterpriseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entreprise supprimée');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleVerifyEnterprise = async (enterpriseId) => {
    try {
      const API = API_URL;
      const token = localStorage.getItem('token');
      await axios.put(`${API}/enterprises/admin/${enterpriseId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entreprise vérifiée et activée !');
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input 
            placeholder="Rechercher une entreprise..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-10 bg-slate-700 border-slate-600 text-white" 
          />
        </div>
      </div>

      {filteredEnterprises.length === 0 ? (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
          <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Aucune entreprise inscrite</h3>
          <p className="text-slate-400">Les entreprises apparaîtront ici après leur inscription</p>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Entreprise</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Contact</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Type</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Documents</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEnterprises.map((enterprise) => (
                <tr key={enterprise.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                  <td className="p-4">
                    <p className="font-medium">{enterprise.company_name}</p>
                    <p className="text-sm text-slate-400">{enterprise.email}</p>
                  </td>
                  <td className="p-4">
                    <p className="text-sm">{enterprise.contact_person}</p>
                    <p className="text-xs text-slate-400">{enterprise.phone}</p>
                  </td>
                  <td className="p-4 capitalize">{enterprise.business_type}</td>
                  <td className="p-4">
                    <div className="flex flex-col gap-1 text-xs">
                      {enterprise.documents?.dfe && (
                        <a href={`${API_BASE}${enterprise.documents.dfe}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                          DFE ✓
                        </a>
                      )}
                      {enterprise.documents?.trade_register && (
                        <a href={`${API_BASE}${enterprise.documents.trade_register}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                          Registre ✓
                        </a>
                      )}
                      {!enterprise.documents?.dfe && !enterprise.documents?.trade_register && (
                        <span className="text-slate-500">Aucun</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col gap-1">
                      {enterprise.is_verified ? (
                        <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Vérifié</span>
                      ) : (
                        <span className="text-amber-400 text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> En attente</span>
                      )}
                      {enterprise.is_active ? <span className="text-green-400 text-xs">Actif</span> : <span className="text-red-400 text-xs">Inactif</span>}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {!enterprise.is_verified && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleVerifyEnterprise(enterprise.id)}>
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteEnterprise(enterprise.id, enterprise.company_name)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProductsSection = ({ products, pendingProducts, filter, setFilter, onApprove, onReject, onToggleFeatured, onDelete, autoApprove, onToggleAutoApprove }) => {
  const featuredCount = products.filter(p => p.is_featured).length;
  const displayProducts = filter === 'pending' ? pendingProducts
    : filter === 'featured' ? products.filter(p => p.is_featured)
    : filter === 'approved' ? products.filter(p => p.status === 'approved')
    : filter === 'rejected' ? products.filter(p => p.status === 'rejected')
    : products;

  return (
    <div className="space-y-4">
      <AutoApproveToggle
        enabled={autoApprove}
        onToggle={onToggleAutoApprove}
        label={`Active : approuve automatiquement tous les produits en attente (${pendingProducts.length} en attente)`}
      />

      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
        <Star className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-300">
          Les produits <strong>en vedette</strong> apparaissent en priorité sur la page d'accueil.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all', label: `Tous (${products.length})` },
          { key: 'pending', label: `En attente (${pendingProducts.length})` },
          { key: 'approved', label: `Approuvés (${products.filter(p => p.status === 'approved').length})` },
          { key: 'featured', label: `⭐ En vedette (${featuredCount})` },
          { key: 'rejected', label: `Rejetés (${products.filter(p => p.status === 'rejected').length})` },
        ].map(({ key, label }) => (
          <Button key={key} variant={filter === key ? 'default' : 'outline'} size="sm" onClick={() => setFilter(key)}
            className={filter === key ? (key === 'featured' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-700') : 'border-slate-600 text-slate-400 hover:text-white'}>
            {label}
          </Button>
        ))}
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700">
        {displayProducts.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Aucun produit</h3>
            <p className="text-slate-400">
              {filter === 'pending' ? 'Aucun produit en attente' : filter === 'featured' ? 'Aucun produit en vedette' : 'Aucun produit'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {displayProducts.map((product) => (
              <div key={product.id} className={`p-4 flex gap-4 items-center transition-colors ${product.is_featured ? 'bg-amber-500/5 border-l-2 border-amber-500' : ''}`}>
                <div className="relative flex-shrink-0">
                  <img src={product.images?.[0] || 'https://via.placeholder.com/80'} alt={product.name} className={`w-16 h-16 rounded-lg object-cover ${product.is_featured ? 'ring-2 ring-amber-400' : ''}`} />
                  {product.is_featured && (
                    <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                      <Star className="w-3.5 h-3.5 text-white fill-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium truncate">{product.name}</h4>
                    {product.is_featured && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full font-semibold">⭐ En vedette</span>}
                  </div>
                  <p className="text-sm text-slate-400 truncate">{product.description}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                    {product.promo_price_fcfa && product.promo_price_fcfa < product.price_fcfa ? (
                      <>
                        <span className="line-through">{formatPrice(product.price_fcfa)} FCFA</span>
                        <span className="text-green-400 font-bold">{formatPrice(product.promo_price_fcfa)} FCFA</span>
                      </>
                    ) : (
                      <span className="text-amber-400 font-bold">{formatPrice(product.price_fcfa)} FCFA</span>
                    )}
                    <span>{product.category_slug}</span>
                    <span>
                      Par : {product.shop_slug ? (
                        <Link
                          to={`/boutique/${product.shop_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:underline"
                        >
                          {product.revendeur_name || product.shop_name || product.seller_name || product.seller?.name || 'Boutique revendeur'}
                        </Link>
                      ) : product.seller_id ? (
                        <Link
                          to={`/vendeur-boutique/${product.seller_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:underline"
                        >
                          {product.seller_name || product.seller?.name || 'Boutique vendeur'}
                        </Link>
                      ) : (
                        product.seller_name || product.revendeur_name || product.shop_name || product.seller?.name || 'Inconnu'
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  <span className={`px-2 py-1 rounded text-xs ${product.status === 'approved' ? 'bg-green-500/20 text-green-400' : product.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                    {product.status}
                  </span>
                  {product.status === 'approved' && (
                    <Button size="sm" variant={product.is_featured ? 'default' : 'outline'}
                      className={product.is_featured ? 'bg-amber-500 hover:bg-amber-600 text-white gap-1' : 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10 gap-1'}
                      onClick={() => onToggleFeatured(product.id)}>
                      <Sparkles className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline text-xs">{product.is_featured ? 'Retirer' : 'Vedette'}</span>
                    </Button>
                  )}
                  {product.status === 'pending' && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onApprove(product.id)}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" onClick={() => onReject(product.id)}><X className="w-4 h-4" /></Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => onDelete(product.id, product.name)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-product-${product.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TransactionsSection = ({ transactions }) => (
  <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
    {transactions.length === 0 ? (
      <div className="p-12 text-center text-slate-500">
        <CreditCard className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>Aucune transaction</p>
      </div>
    ) : (
      <table className="w-full">
        <thead className="bg-slate-700/50">
          <tr>
            <th className="text-left p-4 text-sm font-medium text-slate-400">Date</th>
            <th className="text-left p-4 text-sm font-medium text-slate-400">Utilisateur</th>
            <th className="text-left p-4 text-sm font-medium text-slate-400">Plan</th>
            <th className="text-left p-4 text-sm font-medium text-slate-400">Montant</th>
            <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => (
            <tr key={tx.id} className="border-t border-slate-700">
              <td className="p-4 text-sm">{new Date(tx.created_at).toLocaleDateString('fr-FR')}</td>
              <td className="p-4">
                <p className="text-sm font-medium">{tx.user_name}</p>
                <p className="text-xs text-slate-400">{tx.user_email}</p>
              </td>
              <td className="p-4 capitalize">{tx.plan_id}</td>
              <td className="p-4">
                <p className="font-medium">${tx.amount_usd}</p>
                <p className="text-xs text-slate-400">{formatPrice(tx.amount_fcfa)} FCFA</p>
              </td>
              <td className="p-4">
                <span className={`px-2 py-1 rounded text-xs ${tx.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {tx.payment_status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const PlansSection = ({ plans, vendors }) => {
  const planCounts = plans.reduce((acc, plan) => {
    acc[plan.id] = vendors.filter(v => v.subscription_plan === plan.id).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <div key={plan.id} className={`bg-slate-800 rounded-xl border p-6 ${plan.id === 'entreprise' ? 'border-purple-500/50' : plan.id === 'commercant' ? 'border-amber-500/50' : plan.id === 'artisan' ? 'border-blue-500/50' : 'border-slate-700'}`}>
            <div className="text-3xl mb-2">{plan.emoji}</div>
            <h3 className="font-bold text-lg">{plan.name}</h3>
            <div className="mt-2">
              <p className="text-2xl font-bold">{plan.price_fcfa === 0 ? 'Gratuit' : `${formatPrice(plan.price_fcfa)} FCFA`}</p>
              {plan.price_usd > 0 && <p className="text-xs text-slate-400">~${plan.price_usd}/mois</p>}
            </div>
            <div className="my-4 py-3 border-t border-b border-slate-700">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Vendeurs actifs</span>
                <span className="font-bold">{planCounts[plan.id] || 0}</span>
              </div>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /><span>{plan.max_products === -1 ? 'Produits illimités' : `${plan.max_products} produits max`}</span></li>
              <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /><span>Commission de {plan.commission_percent}%</span></li>
              {plan.badge && <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /><span>Badge {plan.badge}</span></li>}
              {plan.priority_support && <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /><span>Support prioritaire</span></li>}
              {plan.featured_products > 0 && <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /><span>{plan.featured_products} produits en vedette</span></li>}
            </ul>
          </div>
        ))}
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" />Répartition des abonnements</h3>
        <div className="grid md:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const count = planCounts[plan.id] || 0;
            const percentage = vendors.length > 0 ? ((count / vendors.length) * 100).toFixed(1) : 0;
            return (
              <div key={plan.id} className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center justify-between mb-2"><span className="text-2xl">{plan.emoji}</span><span className="text-lg font-bold">{count}</span></div>
                <p className="text-sm text-slate-400">{plan.name}</p>
                <div className="mt-2 h-2 bg-slate-600 rounded-full overflow-hidden">
                  <div className={`h-full ${plan.id === 'entreprise' ? 'bg-purple-500' : plan.id === 'commercant' ? 'bg-amber-500' : plan.id === 'artisan' ? 'bg-blue-500' : 'bg-slate-400'}`} style={{ width: `${percentage}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1">{percentage}%</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-400" />Potentiel de revenus mensuels</h3>
        <div className="overflow-x-auto touch-scroll-x">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-slate-400">
                <th className="p-3">Plan</th><th className="p-3">Prix/mois</th><th className="p-3">Vendeurs</th><th className="p-3">Revenu potentiel</th>
              </tr>
            </thead>
            <tbody>
              {plans.filter(p => p.price_fcfa > 0).map((plan) => {
                const count = planCounts[plan.id] || 0;
                const revenue = count * plan.price_fcfa;
                return (
                  <tr key={plan.id} className="border-t border-slate-700">
                    <td className="p-3"><span className="mr-2">{plan.emoji}</span>{plan.name}</td>
                    <td className="p-3">{formatPrice(plan.price_fcfa)} FCFA</td>
                    <td className="p-3 font-medium">{count}</td>
                    <td className="p-3 font-bold text-green-400">{formatPrice(revenue)} FCFA</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-slate-600 font-bold">
                <td className="p-3" colSpan={3}>Total</td>
                <td className="p-3 text-green-400">
                  {formatPrice(plans.filter(p => p.price_fcfa > 0).reduce((sum, plan) => sum + ((planCounts[plan.id] || 0) * plan.price_fcfa), 0))} FCFA
                 </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SettingsSection = ({ type, settings, onUpdate, onSave }) => {
  if (!settings) return <Skeleton className="h-64 bg-slate-800" />;
  const renderFields = () => {
    switch (type) {
      case 'vendor':
        return (
          <>
            <SettingField label="Max images par produit" value={settings.max_images_per_product} onChange={(v) => onUpdate('max_images_per_product', parseInt(v))} type="number" />
            <SettingField label="Prix minimum (FCFA)" value={settings.min_product_price} onChange={(v) => onUpdate('min_product_price', parseInt(v))} type="number" />
            <SettingField label="Longueur min. description" value={settings.min_description_length} onChange={(v) => onUpdate('min_description_length', parseInt(v))} type="number" />
            <SettingField label="Promo max (%)" value={settings.max_promo_percent} onChange={(v) => onUpdate('max_promo_percent', parseInt(v))} type="number" />
            <SettingToggle label="Auto-approuver vendeurs vérifiés" value={settings.auto_approve_verified} onChange={(v) => onUpdate('auto_approve_verified', v)} />
            <SettingToggle label="Autoriser promotions" value={settings.allow_promotions} onChange={(v) => onUpdate('allow_promotions', v)} />
          </>
        );
      case 'delivery':
        return (
          <>
            <SettingField label="Frais de base (FCFA)" value={settings.base_delivery_fee} onChange={(v) => onUpdate('base_delivery_fee', parseInt(v))} type="number" />
            <SettingField label="Prix par km (FCFA)" value={settings.price_per_km} onChange={(v) => onUpdate('price_per_km', parseInt(v))} type="number" />
            <SettingField label="Rayon max (km)" value={settings.max_delivery_radius_km} onChange={(v) => onUpdate('max_delivery_radius_km', parseInt(v))} type="number" />
            <SettingField label="Note min. pour rester actif" value={settings.min_rating_active} onChange={(v) => onUpdate('min_rating_active', parseFloat(v))} type="number" />
            <SettingToggle label="Vérification permis requise" value={settings.require_license_verification} onChange={(v) => onUpdate('require_license_verification', v)} />
          </>
        );
      case 'platform':
        return (
          <>
            <SettingField label="Nom du site" value={settings.site_name} onChange={(v) => onUpdate('site_name', v)} />
            <SettingField label="Description" value={settings.site_description} onChange={(v) => onUpdate('site_description', v)} />
            <SettingField label="Email de contact" value={settings.contact_email} onChange={(v) => onUpdate('contact_email', v)} />
            <SettingField label="Téléphone" value={settings.contact_phone} onChange={(v) => onUpdate('contact_phone', v)} />
            <SettingField label="Devise" value={settings.currency} onChange={(v) => onUpdate('currency', v)} />
            <SettingToggle label="Mode maintenance" value={settings.maintenance_mode} onChange={(v) => onUpdate('maintenance_mode', v)} />
            <SettingToggle label="Checkout invité" value={settings.allow_guest_checkout} onChange={(v) => onUpdate('allow_guest_checkout', v)} />
          </>
        );
      default: return null;
    }
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
      <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
        <Settings className="w-5 h-5" />
        {type === 'vendor' ? 'Paramètres Vendeurs' : type === 'delivery' ? 'Paramètres Livreurs' : 'Paramètres Généraux'}
      </h3>
      <div className="space-y-4">{renderFields()}</div>
      <div className="mt-6 pt-6 border-t border-slate-700">
        <Button onClick={onSave} className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-2" /> Sauvegarder</Button>
      </div>
    </div>
  );
};

const SettingField = ({ label, value, onChange, type = 'text' }) => (
  <div className="flex items-center justify-between gap-4">
    <label className="text-sm text-slate-400">{label}</label>
    <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-48 bg-slate-700 border-slate-600" />
  </div>
);

const SettingToggle = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between gap-4">
    <label className="text-sm text-slate-400">{label}</label>
    <button onClick={() => onChange(!value)} className={`w-12 h-6 rounded-full transition-colors ${value ? 'bg-green-500' : 'bg-slate-600'}`}>
      <div className={`w-5 h-5 bg-white rounded-full transition-transform mx-0.5 ${value ? 'translate-x-6' : 'translate-x-0'}`} />
    </button>
  </div>
);

const RevendeursSection = ({ revendeurs, stats, transactions, token, onRefresh, onDelete, onMessage, autoApprove, onToggleAutoApprove }) => {
  const [loading, setLoading] = useState(false);

  const handleToggleRevendeur = async (revendeurId) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/revendeurs/${revendeurId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <AutoApproveToggle
        enabled={autoApprove}
        onToggle={onToggleAutoApprove}
        label={`Active : active automatiquement tous les revendeurs inactifs (${revendeurs.filter(r => !r.is_active).length} inactifs)`}
      />

      <h2 className="text-xl font-bold flex items-center gap-2"><Package className="w-6 h-6 text-indigo-400" />Gestion Revente</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700"><p className="text-slate-400 text-sm">Revendeurs actifs</p><p className="text-2xl font-bold text-indigo-400">{stats?.active_revendeurs || 0}</p></div>
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700"><p className="text-slate-400 text-sm">Produits dropshippés</p><p className="text-2xl font-bold text-purple-400">{stats?.total_products || 0}</p></div>
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700"><p className="text-slate-400 text-sm">Revenus Admin</p><p className="text-2xl font-bold text-green-400">{formatPrice(stats?.admin_earnings_fcfa || 0)} F</p></div>
        <div className="bg-slate-800 rounded-xl p-4 border border-slate-700"><p className="text-slate-400 text-sm">Marges totales</p><p className="text-2xl font-bold text-amber-400">{formatPrice(stats?.total_margins_fcfa || 0)} F</p></div>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <div className="p-4 border-b border-slate-700"><h3 className="font-semibold">Liste des Revendeurs ({revendeurs.length})</h3></div>
        <div className="overflow-x-auto touch-scroll-x">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700 text-left text-sm text-slate-400">
                <th className="p-4">Revendeur</th><th className="p-4">Boutique</th><th className="p-4">Produits</th><th className="p-4">Commandes</th><th className="p-4">Gains</th><th className="p-4">Statut</th><th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {revendeurs.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-400">Aucun revendeur inscrit</td></tr>
              ) : (
                revendeurs.map((d) => (
                  <tr key={d.id} className="border-b border-slate-700 hover:bg-slate-700/50">
                    <td className="p-4"><div><p className="font-medium">{d.name}</p><p className="text-sm text-slate-400">{d.email}</p></div></td>
                    <td className="p-4"><span className="text-indigo-400">{d.shop_name}</span></td>
                    <td className="p-4">{d.product_count || 0}</td>
                    <td className="p-4">{d.order_count || 0}</td>
                    <td className="p-4 text-green-400">{formatPrice(d.total_earnings || 0)} F</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${d.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{d.is_active ? 'Actif' : 'Inactif'}</span></td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {d.shop_slug && (
                          <Button asChild size="sm" variant="outline" className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10">
                            <Link to={`/boutique/${d.shop_slug}`} target="_blank" rel="noopener noreferrer">
                              <Eye className="w-4 h-4 mr-1" /> Voir la boutique
                            </Link>
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10" onClick={() => onMessage(d)}>
                          Écrire
                        </Button>
                        <Button size="sm" variant={d.is_active ? 'destructive' : 'default'} onClick={() => handleToggleRevendeur(d.id)} disabled={loading}>
                          {d.is_active ? 'Désactiver' : 'Activer'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDelete(d.id, d.shop_name || d.name)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-revendeur-${d.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                     </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700">
        <div className="p-4 border-b border-slate-700"><h3 className="font-semibold">Transactions Revente récentes</h3></div>
        <div className="p-4 space-y-3">
          {transactions.length === 0 ? (
            <p className="text-slate-400 text-center py-4">Aucune transaction</p>
          ) : (
            transactions.slice(0, 10).map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                <div><p className="font-medium">{t.product_name}</p><p className="text-sm text-slate-400">{t.revendeur_name} · {t.order_number}</p></div>
                <div className="text-right"><p className="text-green-400 font-medium">+{formatPrice(t.admin_share || 0)} F</p><p className="text-xs text-slate-400">sur {formatPrice(t.total_margin || 0)} F de marge</p></div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const UsersSection = ({ users, roleFilter, setRoleFilter, search, setSearch, onDelete, onToggleActive }) => {
  const roleLabels = { all: 'Tous', customer: 'Clients', vendor: 'Vendeurs', driver: 'Livreurs', revendeur: 'Revendeurs' };
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400';
      case 'vendor': return 'bg-purple-500/20 text-purple-400';
      case 'driver': return 'bg-blue-500/20 text-blue-400';
      case 'revendeur': return 'bg-indigo-500/20 text-indigo-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-rose-400" />Gestion des Utilisateurs ({users.length})</h2>
      <div className="flex flex-wrap gap-4">
        <div className="flex gap-2 flex-wrap">
          {Object.entries(roleLabels).map(([key, label]) => (
            <Button key={key} size="sm" variant={roleFilter === key ? 'default' : 'outline'} onClick={() => setRoleFilter(key)} className={roleFilter === key ? 'bg-rose-600 hover:bg-rose-700' : ''}>{label}</Button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-slate-800 border-slate-700" />
        </div>
      </div>
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto touch-scroll-x">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700 text-left text-sm text-slate-400">
                <th className="p-4">Utilisateur</th><th className="p-4">Rôle</th><th className="p-4">Téléphone</th><th className="p-4">Produits</th><th className="p-4">Statut</th><th className="p-4">Inscrit le</th><th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-400">Aucun utilisateur trouvé</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-700 hover:bg-slate-700/50">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-white font-bold">{u.name?.[0]?.toUpperCase() || '?'}</div>
                        <div><p className="font-medium">{u.name}</p><p className="text-sm text-slate-400">{u.email}</p></div>
                      </div>
                     </td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${getRoleColor(u.role)}`}>{roleLabels[u.role] || u.role}</span></td>
                    <td className="p-4 text-slate-300">{u.phone || '-'}</td>
                    <td className="p-4">{u.product_count || 0}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${u.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{u.is_active ? 'Actif' : 'Désactivé'}</span></td>
                    <td className="p-4 text-slate-400 text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="p-4">
                      {u.role !== 'admin' && (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => onToggleActive(u.id, u.name)} className="text-xs">{u.is_active ? <Ban className="w-3 h-3" /> : <Check className="w-3 h-3" />}</Button>
                          <Button size="sm" variant="destructive" onClick={() => onDelete(u.id, u.name)} className="text-xs"><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      )}
                     </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PREDEFINED FIELD TEMPLATES for category custom fields
// ============================================================
const PREDEFINED_FIELD_TEMPLATES = [
  {
    key: 'tailles',
    label: 'Tailles',
    icon: Ruler,
    color: 'text-blue-400',
    field_type: 'multiselect',
    options: [
      { label: 'XS', value: 'xs' }, { label: 'S', value: 's' }, { label: 'M', value: 'm' },
      { label: 'L', value: 'l' }, { label: 'XL', value: 'xl' }, { label: 'XXL', value: 'xxl' },
      { label: '3XL', value: '3xl' }, { label: '4XL', value: '4xl' },
    ],
    placeholder: 'Sélectionner les tailles disponibles',
  },
  {
    key: 'pointures',
    label: 'Pointures',
    icon: Footprints,
    color: 'text-orange-400',
    field_type: 'multiselect',
    options: Array.from({ length: 16 }, (_, i) => ({ label: String(35 + i), value: String(35 + i) })),
    placeholder: 'Sélectionner les pointures disponibles',
  },
  {
    key: 'couleurs',
    label: 'Couleurs',
    icon: Palette,
    color: 'text-pink-400',
    field_type: 'multiselect',
    options: [
      { label: 'Noir', value: 'noir' }, { label: 'Blanc', value: 'blanc' }, { label: 'Rouge', value: 'rouge' },
      { label: 'Bleu', value: 'bleu' }, { label: 'Vert', value: 'vert' }, { label: 'Jaune', value: 'jaune' },
      { label: 'Rose', value: 'rose' }, { label: 'Orange', value: 'orange' }, { label: 'Violet', value: 'violet' },
      { label: 'Gris', value: 'gris' }, { label: 'Marron', value: 'marron' }, { label: 'Beige', value: 'beige' },
      { label: 'Doré', value: 'dore' }, { label: 'Argenté', value: 'argente' }, { label: 'Multicolore', value: 'multicolore' },
    ],
    placeholder: 'Sélectionner les couleurs disponibles',
  },
  {
    key: 'matiere',
    label: 'Matière',
    icon: Shirt,
    color: 'text-emerald-400',
    field_type: 'select',
    options: [
      { label: 'Coton', value: 'coton' }, { label: 'Polyester', value: 'polyester' }, { label: 'Soie', value: 'soie' },
      { label: 'Lin', value: 'lin' }, { label: 'Laine', value: 'laine' }, { label: 'Cuir', value: 'cuir' },
      { label: 'Cuir synthétique', value: 'cuir-synthetique' }, { label: 'Jean/Denim', value: 'denim' },
      { label: 'Satin', value: 'satin' }, { label: 'Velours', value: 'velours' }, { label: 'Wax', value: 'wax' },
      { label: 'Bazin', value: 'bazin' }, { label: 'Kente', value: 'kente' }, { label: 'Autre', value: 'autre' },
    ],
    placeholder: 'Sélectionner la matière',
  },
  {
    key: 'genre',
    label: 'Genre',
    icon: Users,
    color: 'text-violet-400',
    field_type: 'select',
    options: [
      { label: 'Homme', value: 'homme' }, { label: 'Femme', value: 'femme' },
      { label: 'Enfant', value: 'enfant' }, { label: 'Mixte / Unisexe', value: 'mixte' },
    ],
    placeholder: 'Sélectionner le genre',
  },
  {
    key: 'poids',
    label: 'Poids (kg)',
    icon: Weight,
    color: 'text-amber-400',
    field_type: 'number',
    options: [],
    placeholder: 'Ex: 0.5',
  },
  {
    key: 'dimensions',
    label: 'Dimensions (L x l x H)',
    icon: Box,
    color: 'text-cyan-400',
    field_type: 'text',
    options: [],
    placeholder: 'Ex: 30 x 20 x 10 cm',
  },
  {
    key: 'marque',
    label: 'Marque',
    icon: Tag,
    color: 'text-rose-400',
    field_type: 'text',
    options: [],
    placeholder: 'Ex: Nike, Adidas...',
  },
  {
    key: 'capacite',
    label: 'Capacité / Volume',
    icon: Box,
    color: 'text-teal-400',
    field_type: 'text',
    options: [],
    placeholder: 'Ex: 500ml, 1L, 32GB...',
  },
];

// ============================================================
// CustomFieldsBuilder - Premium UI component
// ============================================================
const CustomFieldsBuilder = ({ fields, onChange, compact = false }) => {
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customFieldName, setCustomFieldName] = useState('');
  const [customFieldType, setCustomFieldType] = useState('text');
  const [customFieldOptions, setCustomFieldOptions] = useState('');
  const [expandedField, setExpandedField] = useState(null);

  const activeKeys = new Set((fields || []).map(f => f.key));

  const togglePredefined = (template) => {
    const current = fields || [];
    if (activeKeys.has(template.key)) {
      onChange(current.filter(f => f.key !== template.key));
    } else {
      onChange([...current, {
        key: template.key,
        label: template.label,
        field_type: template.field_type,
        options: template.options,
        required: false,
        placeholder: template.placeholder || '',
      }]);
    }
  };

  const addCustomField = () => {
    if (!customFieldName.trim()) return;
    const key = customFieldName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (activeKeys.has(key)) { toast.error('Ce champ existe déjà'); return; }

    const options = (customFieldType === 'select' || customFieldType === 'multiselect')
      ? customFieldOptions.split(',').map(o => o.trim()).filter(Boolean).map(o => ({ label: o, value: o.toLowerCase().replace(/\s+/g, '-') }))
      : [];

    onChange([...(fields || []), {
      key,
      label: customFieldName.trim(),
      field_type: customFieldType,
      options,
      required: false,
      placeholder: '',
    }]);
    setCustomFieldName('');
    setCustomFieldType('text');
    setCustomFieldOptions('');
    setShowAddCustom(false);
  };

  const removeField = (key) => {
    onChange((fields || []).filter(f => f.key !== key));
  };

  const toggleRequired = (key) => {
    onChange((fields || []).map(f => f.key === key ? { ...f, required: !f.required } : f));
  };

  const updateFieldOptions = (key, newOptions) => {
    onChange((fields || []).map(f => f.key === key ? { ...f, options: newOptions } : f));
  };

  const addOptionToField = (key, optionLabel) => {
    if (!optionLabel.trim()) return;
    const current = (fields || []).find(f => f.key === key);
    if (!current) return;
    const newOpt = { label: optionLabel.trim(), value: optionLabel.trim().toLowerCase().replace(/\s+/g, '-') };
    updateFieldOptions(key, [...(current.options || []), newOpt]);
  };

  const removeOptionFromField = (key, optionValue) => {
    const current = (fields || []).find(f => f.key === key);
    if (!current) return;
    updateFieldOptions(key, (current.options || []).filter(o => o.value !== optionValue));
  };

  return (
    <div className={`mt-${compact ? '3' : '6'} space-y-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Settings className="w-5 h-5 text-teal-400" />
        <h4 className="font-semibold text-sm text-slate-200">Champs personnalisés</h4>
        <span className="text-xs text-slate-500 ml-auto">{(fields || []).length} champ(s) activé(s)</span>
      </div>

      {/* Predefined field templates as toggleable cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {PREDEFINED_FIELD_TEMPLATES.map((tpl) => {
          const isActive = activeKeys.has(tpl.key);
          const Icon = tpl.icon;
          return (
            <button
              key={tpl.key}
              type="button"
              onClick={() => togglePredefined(tpl)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-all text-sm ${
                isActive
                  ? 'bg-teal-900/50 border-teal-500/60 text-teal-300 shadow-lg shadow-teal-500/10'
                  : 'bg-slate-800/50 border-slate-600/40 text-slate-400 hover:border-slate-500 hover:text-slate-300'
              }`}
            >
              <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-teal-400' : tpl.color}`} />
              <span className="truncate">{tpl.label}</span>
              {isActive && <Check className="w-3.5 h-3.5 ml-auto text-teal-400 flex-shrink-0" />}
            </button>
          );
        })}
      </div>

      {/* Active fields detail list */}
      {(fields || []).length > 0 && (
        <div className="space-y-2 mt-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Champs activés</p>
          {(fields || []).map((field) => {
            const isExpanded = expandedField === field.key;
            const predefined = PREDEFINED_FIELD_TEMPLATES.find(t => t.key === field.key);
            const Icon = predefined?.icon || Tag;
            return (
              <div key={field.key} className="bg-slate-900/60 rounded-lg border border-slate-700/50 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3">
                  <Icon className={`w-4 h-4 flex-shrink-0 ${predefined?.color || 'text-slate-400'}`} />
                  <span className="font-medium text-sm text-slate-200 flex-1">{field.label}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-400">{field.field_type}</span>
                  <button
                    type="button"
                    onClick={() => toggleRequired(field.key)}
                    className={`text-xs px-2 py-0.5 rounded transition-colors ${field.required ? 'bg-red-900/50 text-red-400 border border-red-500/30' : 'bg-slate-700/50 text-slate-500 hover:text-slate-300'}`}
                  >
                    {field.required ? 'Requis' : 'Optionnel'}
                  </button>
                  {(field.field_type === 'select' || field.field_type === 'multiselect') && (
                    <button type="button" onClick={() => setExpandedField(isExpanded ? null : field.key)} className="text-slate-400 hover:text-slate-200">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                  <button type="button" onClick={() => removeField(field.key)} className="text-red-400/60 hover:text-red-400 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Expandable options editor */}
                {isExpanded && (field.field_type === 'select' || field.field_type === 'multiselect') && (
                  <div className="px-4 pb-3 border-t border-slate-700/30 pt-3">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(field.options || []).map((opt) => (
                        <span key={opt.value} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-700/50 rounded text-xs text-slate-300">
                          {opt.label}
                          <button type="button" onClick={() => removeOptionFromField(field.key, opt.value)} className="text-red-400/50 hover:text-red-400">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ajouter une option..."
                        className="h-8 text-xs bg-slate-800"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOptionToField(field.key, e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add custom field button & form */}
      {!showAddCustom ? (
        <button
          type="button"
          onClick={() => setShowAddCustom(true)}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors mt-2"
        >
          <Plus className="w-4 h-4" /> Ajouter un champ personnalisé
        </button>
      ) : (
        <div className="bg-slate-900/60 rounded-lg border border-teal-500/20 p-4 space-y-3">
          <h5 className="text-sm font-medium text-teal-400">Nouveau champ personnalisé</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              value={customFieldName}
              onChange={(e) => setCustomFieldName(e.target.value)}
              placeholder="Nom du champ"
              className="bg-slate-800 text-sm"
            />
            <select
              value={customFieldType}
              onChange={(e) => setCustomFieldType(e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded-md p-2 text-white text-sm"
            >
              <option value="text">Texte libre</option>
              <option value="number">Nombre</option>
              <option value="select">Liste déroulante</option>
              <option value="multiselect">Sélection multiple</option>
            </select>
            {(customFieldType === 'select' || customFieldType === 'multiselect') && (
              <Input
                value={customFieldOptions}
                onChange={(e) => setCustomFieldOptions(e.target.value)}
                placeholder="Options (séparées par des virgules)"
                className="bg-slate-800 text-sm"
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={addCustomField} className="bg-teal-600 hover:bg-teal-700 text-sm">
              Ajouter
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAddCustom(false)} className="text-sm">
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const CategoriesSection = ({ 
  token, 
  categories, 
  editingCategory, 
  setEditingCategory, 
  newCategory, 
  setNewCategory, 
  showNewForm, 
  setShowNewForm, 
  onCreate, 
  onUpdate, 
  onDelete, 
  onToggle,
  onRefresh
}) => {
  const [showSubForm, setShowSubForm] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const [newSubCategory, setNewSubCategory] = useState({ 
    name: '', 
    description: '', 
    parent_slug: '', 
    banner_images: [],
    custom_fields: [],
  });

  const parentCategories = categories.filter(c => !c.parent_slug);
  const getSubCategories = (parentSlug) => categories.filter(c => c.parent_slug === parentSlug);

  const handleCreateSubCategory = async () => {
    if (!newSubCategory.name || !newSubCategory.parent_slug) {
      toast.error('Le nom et la catégorie parente sont obligatoires');
      return;
    }

    try {
      const parent = parentCategories.find(p => p.slug === newSubCategory.parent_slug);
      const baseSlug = newSubCategory.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const finalSlug = parent ? `${parent.slug}-${baseSlug}` : baseSlug;

      const payload = {
        name: newSubCategory.name,
        slug: finalSlug,
        description: newSubCategory.description || '',
        banner_images: newSubCategory.banner_images || [],
        parent_slug: newSubCategory.parent_slug,
        icon: 'Package',
        custom_fields: newSubCategory.custom_fields || [],
      };

      await axios.post(`${API}/admin/categories`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Sous-catégorie créée avec succès !');
      
      setNewSubCategory({ name: '', description: '', parent_slug: '', banner_images: [], custom_fields: [] });
      setShowSubForm(false);
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error(error.response?.data);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la sous-catégorie');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Cog className="w-6 h-6 text-teal-400" />
          Catégories & Sous-catégories ({categories.length})
        </h2>
        <div className="flex gap-3">
          <Button onClick={() => { setShowSubForm(true); setShowNewForm(false); }} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle sous-catégorie
          </Button>
          <Button onClick={() => { setShowNewForm(true); setShowSubForm(false); }} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle catégorie
          </Button>
        </div>
      </div>

      {/* Formulaire Catégorie principale */}
      {showNewForm && (
        <div className="bg-slate-800 rounded-xl border border-teal-500/30 p-6">
          <h3 className="font-semibold mb-4 text-teal-400">Créer une Catégorie principale</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Nom de la catégorie *</label>
              <Input 
                value={newCategory.name} 
                onChange={(e) => setNewCategory({...newCategory, name: e.target.value})} 
                placeholder="Ex: Mode" 
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Description</label>
              <Input 
                value={newCategory.description} 
                onChange={(e) => setNewCategory({...newCategory, description: e.target.value})} 
              />
            </div>
          </div>

          <div className="mt-5">
            <ImageUpload 
              images={newCategory.banner_images || []}
              onChange={(images) => setNewCategory({ ...newCategory, banner_images: images.slice(0, 3) })}
              maxImages={3}
              token={token}
              label="3 photos de mise en avant (bannière)"
              hint="Ces images défileront en haut de la catégorie"
            />
          </div>

          {/* Custom Fields Builder */}
          <CustomFieldsBuilder
            fields={newCategory.custom_fields || []}
            onChange={(fields) => setNewCategory({ ...newCategory, custom_fields: fields })}
          />

          <div className="flex gap-3 mt-6">
            <Button onClick={onCreate} className="bg-teal-600 hover:bg-teal-700">
              Créer la catégorie
            </Button>
            <Button variant="outline" onClick={() => setShowNewForm(false)}>Annuler</Button>
          </div>
        </div>
      )}

      {/* Formulaire Sous-catégorie */}
      {showSubForm && (
        <div className="bg-slate-800 rounded-xl border border-purple-500/30 p-6">
          <h3 className="font-semibold mb-4 text-purple-400">Créer une Sous-catégorie</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Catégorie parente *</label>
              <select 
                value={newSubCategory.parent_slug} 
                onChange={(e) => setNewSubCategory({...newSubCategory, parent_slug: e.target.value})}
                className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-white"
              >
                <option value="">Sélectionner une catégorie</option>
                {parentCategories.map(cat => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Nom de la sous-catégorie *</label>
              <Input 
                value={newSubCategory.name} 
                onChange={(e) => setNewSubCategory({...newSubCategory, name: e.target.value})} 
                placeholder="Ex: Robes Traditionnelles" 
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Description</label>
              <Input 
                value={newSubCategory.description} 
                onChange={(e) => setNewSubCategory({...newSubCategory, description: e.target.value})} 
              />
            </div>
          </div>

          <div className="mt-5">
            <ImageUpload 
              images={newSubCategory.banner_images || []}
              onChange={(images) => setNewSubCategory({ ...newSubCategory, banner_images: images.slice(0, 3) })}
              maxImages={3}
              token={token}
              label="3 photos de mise en avant (bannière)"
              hint="Ces images défileront en haut de la sous-catégorie"
            />
          </div>

          {/* Custom Fields Builder for sub-category */}
          <CustomFieldsBuilder
            fields={newSubCategory.custom_fields || []}
            onChange={(fields) => setNewSubCategory({ ...newSubCategory, custom_fields: fields })}
          />

          <div className="flex gap-3 mt-6">
            <Button onClick={handleCreateSubCategory} className="bg-purple-600 hover:bg-purple-700">
              Créer la sous-catégorie
            </Button>
            <Button variant="outline" onClick={() => setShowSubForm(false)}>Annuler</Button>
          </div>
        </div>
      )}

      {/* Formulaire d'édition */}
      {editingCategory && (
        <div className="bg-slate-800 rounded-xl border border-amber-500/30 p-6">
          <h3 className="font-semibold mb-4 text-amber-400">
            {editingCategory.parent_slug ? 'Modifier la Sous-catégorie' : 'Modifier la Catégorie'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Nom *</label>
              <Input 
                value={editingCategory.name || ''} 
                onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})} 
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Description</label>
              <Input 
                value={editingCategory.description || ''} 
                onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})} 
              />
            </div>
            {editingCategory.parent_slug && (
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Catégorie parente</label>
                <select 
                  value={editingCategory.parent_slug || ''} 
                  onChange={(e) => setEditingCategory({...editingCategory, parent_slug: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-white"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {parentCategories.map(cat => (
                    <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="mt-5">
            <ImageUpload 
              images={editingCategory.banner_images || []}
              onChange={(images) => setEditingCategory({ ...editingCategory, banner_images: images.slice(0, 3) })}
              maxImages={3}
              token={token}
              label="3 photos de mise en avant (bannière)"
              hint="Ces images défileront en haut"
            />
          </div>

          {/* Custom Fields Builder for editing */}
          <CustomFieldsBuilder
            fields={editingCategory.custom_fields || []}
            onChange={(fields) => setEditingCategory({ ...editingCategory, custom_fields: fields })}
          />

          <div className="flex gap-3 mt-6">
            <Button onClick={() => onUpdate(editingCategory.id)} className="bg-amber-600 hover:bg-amber-700">
              Mettre à jour
            </Button>
            <Button variant="outline" onClick={() => setEditingCategory(null)}>Annuler</Button>
          </div>
        </div>
      )}

      {/* Liste des catégories */}
      <div className="space-y-4">
        {parentCategories.map((cat) => {
          const subCats = getSubCategories(cat.slug);
          const isExpanded = expandedCategory === cat.id;
          const fieldCount = (cat.custom_fields || []).length;
          return (
            <div key={cat.id} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
              <div 
                className="p-5 flex items-center justify-between bg-slate-700/40 cursor-pointer hover:bg-slate-700/60 transition-colors"
                onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
              >
                <div className="flex items-center gap-3">
                  <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  <div>
                    <h3 className="font-bold text-lg">{cat.name}</h3>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <span>{subCats.length} sous-catégorie{subCats.length > 1 ? 's' : ''}</span>
                      {fieldCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-900/30 text-teal-400 text-xs">
                          <Settings className="w-3 h-3" /> {fieldCount} champ{fieldCount > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {(cat.banner_images || []).slice(0,3).map((img,i) => (
                    <img key={i} src={img} className="w-12 h-12 object-cover rounded" alt="" />
                  ))}
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" onClick={() => setEditingCategory({...cat, custom_fields: cat.custom_fields || []})}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => onToggle(cat.id)}>
                      {cat.is_active !== false ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => onDelete(cat.id, cat.name)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>

              {/* Sous-catégories */}
              {isExpanded && (
                <div className="p-4 pl-8 bg-slate-900/30">
                  {/* Show parent custom fields summary */}
                  {fieldCount > 0 && (
                    <div className="mb-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700/30">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Champs de la catégorie</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(cat.custom_fields || []).map(f => {
                          const tpl = PREDEFINED_FIELD_TEMPLATES.find(t => t.key === f.key);
                          const Icon = tpl?.icon || Tag;
                          return (
                            <span key={f.key} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700/60 text-xs text-slate-300">
                              <Icon className={`w-3 h-3 ${tpl?.color || 'text-slate-400'}`} /> {f.label}
                              {f.required && <span className="text-red-400">*</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {subCats.length > 0 ? subCats.map(sub => {
                    const subFieldCount = (sub.custom_fields || []).length;
                    return (
                      <div key={sub.id} className="flex justify-between items-center p-3 hover:bg-slate-700/50 rounded-lg mb-1">
                        <div className="flex items-center gap-3">
                          <span className="text-purple-300">{sub.name}</span>
                          {subFieldCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-900/30 text-purple-400 text-xs">
                              <Settings className="w-3 h-3" /> {subFieldCount} champ{subFieldCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingCategory({...sub, custom_fields: sub.custom_fields || []})}><Edit className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => onToggle(sub.id)}>
                            {sub.is_active !== false ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => onDelete(sub.id, sub.name)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    );
                  }) : (
                    <p className="text-sm text-slate-500 italic py-2">Aucune sous-catégorie</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AdminMessagesSection = ({ conversations, onRefresh, onOpenConversation }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Messages ({conversations.length})</h2>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto touch-scroll-x">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Conversation</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Dernier message</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Date</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Non lus</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Action</th>
              </tr>
            </thead>
            <tbody>
              {conversations.map((conv) => (
                <tr key={conv.id} className="border-t border-slate-700 hover:bg-slate-700/20">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {conv?.seller_avatar ? (
                        <MediaImg 
                          src={conv.seller_avatar} 
                          alt={conv.seller_name}
                          className="w-10 h-10 rounded-full object-cover border-2 border-slate-600"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-500 flex items-center justify-center text-white font-bold">
                          {conv?.seller_name?.[0] || "U"}
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{conv.seller_name || 'Utilisateur'}</p>
                        <p className="text-xs text-slate-400">{conv.product_name || 'Message direct'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-slate-300">{conv.last_message || 'Aucun message'}</td>
                  <td className="p-4 text-sm text-slate-400">
                    {conv.updated_at ? new Date(conv.updated_at).toLocaleString('fr-FR') : '-'}
                  </td>
                  <td className="p-4">
                    {(conv.unread_customer || 0) > 0 ? (
                      <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 text-xs font-bold">
                        {conv.unread_customer}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-500">0</span>
                    )}
                  </td>
                  <td className="p-4">
                    <Button size="sm" onClick={() => onOpenConversation(conv.id)} className="bg-fuchsia-600 hover:bg-fuchsia-700">
                      Ouvrir le chat
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {conversations.length === 0 && (
          <div className="p-12 text-center text-slate-500">Aucune conversation pour le moment</div>
        )}
      </div>
    </div>
  );
};

const SupportSection = ({ supportRequests, onRefresh }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Demandes de Support ({supportRequests.length})</h2>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto touch-scroll-x">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Client</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Catégorie</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Sujet</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Message</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Date</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Action</th>
              </tr>
            </thead>
            <tbody>
              {supportRequests.map((request) => (
                <tr key={request._id} className="border-t border-slate-700 hover:bg-slate-700/20">
                  <td className="p-4">
                    <div>
                      <p className="font-medium">{request.name}</p>
                      <p className="text-xs text-slate-400">{request.email}</p>
                      {request.user_id !== 'Guest' && (
                        <p className="text-xs text-purple-400">ID: {request.user_id}</p>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="bg-purple-500/20 text-purple-300 text-xs px-2 py-1 rounded-full">
                      {request.category}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-slate-300">{request.subject}</td>
                  <td className="p-4 text-sm text-slate-300 max-w-xs truncate">{request.message}</td>
                  <td className="p-4 text-sm text-slate-400">
                    {request.created_at ? new Date(request.created_at).toLocaleString('fr-FR') : '-'}
                  </td>
                  <td className="p-4">
                    {request.email_sent ? (
                      <span className="bg-green-500/20 text-green-300 text-xs px-2 py-1 rounded-full">Email envoyé</span>
                    ) : (
                      <span className="bg-yellow-500/20 text-yellow-300 text-xs px-2 py-1 rounded-full">En attente</span>
                    )}
                  </td>
                  <td className="p-4">
                    <Button variant="outline" size="sm" onClick={() => window.open(`mailto:${request.email}?subject=Re: ${request.subject}`, '_blank')}>
                      <MessageCircle className="w-4 h-4 mr-2" /> Répondre
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {supportRequests.length === 0 && (
          <div className="p-12 text-center text-slate-500">Aucune demande de support pour le moment</div>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// COMPOSANT : APPARENCE DU SITE (ADMIN)
// ──────────────────────────────────────────────────────────────
// Ce composant permet à l'administrateur de configurer tous les
// éléments visuels du site : logo, carrousel hero, blocs publicitaires,
// et bloc "Tendances du moment".
// ──────────────────────────────────────────────────────────────

const LayoutAppearanceSection = ({ token, API }) => {
  // ============================================================
  // 1. CONFIGURATION PAR DÉFAUT DES 4 BLOCS PUBLICITAIRES HORIZONTAUX
  //    (Situés sous la zone hero - 4 blocs côte à côte)
  // ============================================================
  const defaultAdStrips = [
    { id: 'offers', label: 'Offres du Jour', title: 'Espace Publicitaire - Offres du Jour', subtitle: 'Mettez ici vos promos, annonces flash et nouveautés sponsorisées.', tone: 'orange', enabled: true, media_type: 'none', media_url: '', link: '' },
    { id: 'partners', label: 'Marques Partenaires', title: 'Espace Publicitaire - Marques Partenaires', subtitle: 'Zone dédiée aux campagnes partenaires, bannières saisonnières et bons plans.', tone: 'blue', enabled: true, media_type: 'none', media_url: '', link: '' },
    { id: 'premium', label: 'Sélection Premium', title: 'Espace Publicitaire - Sélection Premium', subtitle: 'Emplacements premium pour opérations spéciales, événements et mises en avant.', tone: 'green', enabled: true, media_type: 'none', media_url: '', link: '' },
    { id: 'flash', label: 'Ventes Flash', title: 'Espace Publicitaire - Ventes Flash', subtitle: 'Offres limitées dans le temps, ne manquez pas ces bonnes affaires !', tone: 'red', enabled: true, media_type: 'none', media_url: '', link: '' },
  ];

  // ============================================================
  // 2. ÉTATS DU COMPOSANT
  // ============================================================

  // --- 2.1 CARROUSEL HERO (Diaporama principal) ---
  const [heroImages, setHeroImages] = React.useState([]);
  const [heroLoading, setHeroLoading] = React.useState(false);
  const [heroUploading, setHeroUploading] = React.useState(false);
  const [dragOverIndex, setDragOverIndex] = React.useState(null);

  // --- 2.2 BLOCS PUBLICITAIRES HORIZONTAUX (4 blocs sous la hero) ---
  const [adStrips, setAdStrips] = React.useState(defaultAdStrips);
  const [adStripsLoading, setAdStripsLoading] = React.useState(false);
  const [adStripsSaving, setAdStripsSaving] = React.useState(false);
  const [adStripUploading, setAdStripUploading] = React.useState(null);

  // --- 2.3 BLOC PUBLICITAIRE DROITE - HAUT (Colonne droite, bloc supérieur) ---
  const [rightBlockTopType, setRightBlockTopType] = React.useState('image');
  const [rightBlockTopImage, setRightBlockTopImage] = React.useState('');
  const [rightBlockTopVideo, setRightBlockTopVideo] = React.useState('');
  const [rightBlockTopTitle, setRightBlockTopTitle] = React.useState('Espace publicitaire');
  const [rightBlockTopLink, setRightBlockTopLink] = React.useState('');
  const [rightBlockTopLoading, setRightBlockTopLoading] = React.useState(false);
  const [rightBlockTopSaving, setRightBlockTopSaving] = React.useState(false);
  const [rightBlockTopUploading, setRightBlockTopUploading] = React.useState(false);

  // --- 2.4 BLOC PUBLICITAIRE DROITE - BAS (Colonne droite, bloc inférieur) ---
  const [rightBlockBottomType, setRightBlockBottomType] = React.useState('image');
  const [rightBlockBottomImage, setRightBlockBottomImage] = React.useState('');
  const [rightBlockBottomVideo, setRightBlockBottomVideo] = React.useState('');
  const [rightBlockBottomTitle, setRightBlockBottomTitle] = React.useState('Espace publicitaire');
  const [rightBlockBottomLink, setRightBlockBottomLink] = React.useState('');
  const [rightBlockBottomLoading, setRightBlockBottomLoading] = React.useState(false);
  const [rightBlockBottomSaving, setRightBlockBottomSaving] = React.useState(false);
  const [rightBlockBottomUploading, setRightBlockBottomUploading] = React.useState(false);

  // --- 2.5 BLOC TENDANCES DU MOMENT ---
  const [trendingGradientFrom, setTrendingGradientFrom] = React.useState('#1e293b');
  const [trendingGradientTo, setTrendingGradientTo] = React.useState('#0f172a');
  const [trendingBackgroundImage, setTrendingBackgroundImage] = React.useState('');
  const [trendingEnableBlurs, setTrendingEnableBlurs] = React.useState(true);
  const [trendingLoading, setTrendingLoading] = React.useState(false);
  const [trendingUploading, setTrendingUploading] = React.useState(false);
  const [trendingSaving, setTrendingSaving] = React.useState(false);

  // --- 2.6 LOGO DU SITE ---
  const [logoUrl, setLogoUrl] = React.useState('');
  const [logoLoading, setLogoLoading] = React.useState(false);
  const [logoSaving, setLogoSaving] = React.useState(false);
  const [logoUploading, setLogoUploading] = React.useState(false);

  // --- 2.7 FOND DE PAGE DE CONNEXION ---
  const [authPageEnabled, setAuthPageEnabled] = React.useState(false);
  const [authPageBackgroundType, setAuthPageBackgroundType] = React.useState('color');
  const [authPageBgColor, setAuthPageBgColor] = React.useState('');
  const [authPageBgImages, setAuthPageBgImages] = React.useState([]);
  const [authPageLayoutType, setAuthPageLayoutType] = React.useState('single');
  const [authPageLoading, setAuthPageLoading] = React.useState(false);
  const [authPageSaving, setAuthPageSaving] = React.useState(false);
  const [authPageUploading, setAuthPageUploading] = React.useState(false);

  // ============================================================
  // 3. FONCTIONS DU CARROUSEL HERO
  // ============================================================
  const fetchHeroImages = async () => {
    setHeroLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/hero`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const images = response.data.images || [];
      if (images.length > 0 && typeof images[0] === 'string') {
        setHeroImages(images.map(url => ({ url, link: '', title: '' })));
      } else {
        setHeroImages(images);
      }
    } catch (error) {
      console.error('Erreur chargement hero images:', error);
      toast.error('Impossible de charger les images du hero');
    } finally {
      setHeroLoading(false);
    }
  };

  const saveHeroImages = async (images) => {
    try {
      await axios.put(`${API}/admin/settings/hero`, { images }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Erreur sauvegarde hero:', error);
      toast.error('Erreur lors de la sauvegarde');
      throw error;
    }
  };

  const uploadHeroImage = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez GIF, PNG, JPEG ou JPG');
      return;
    }

    const formData = new FormData();
    formData.append('files', file);

    setHeroUploading(true);
    try {
      const response = await axios.post(`${API}/upload/multiple`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      const newImageUrl = response.data.urls?.[0] || '';
      const updatedImages = [...heroImages, { url: newImageUrl, link: '', title: '' }];
      setHeroImages(updatedImages);
      await saveHeroImages(updatedImages);
      toast.success('Image ajoutée au carrousel !');
    } catch (error) {
      console.error('Erreur upload hero:', error);
      const errorMsg = error.response?.data?.detail || "Erreur lors de l'upload";
      toast.error(errorMsg);
    } finally {
      setHeroUploading(false);
    }
  };

  const removeHeroImage = async (indexToRemove) => {
    const updatedImages = heroImages.filter((_, idx) => idx !== indexToRemove);
    setHeroImages(updatedImages);
    await saveHeroImages(updatedImages);
    toast.success('Image supprimée');
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(sourceIndex) || sourceIndex === targetIndex) {
      setDragOverIndex(null);
      return;
    }

    const newImages = [...heroImages];
    const [removed] = newImages.splice(sourceIndex, 1);
    newImages.splice(targetIndex, 0, removed);
    setHeroImages(newImages);
    setDragOverIndex(null);
    await saveHeroImages(newImages);
    toast.success('Ordre mis à jour');
  };

  const updateHeroImageLink = (index, link) => {
    const updatedImages = [...heroImages];
    updatedImages[index] = { ...updatedImages[index], link };
    setHeroImages(updatedImages);
    saveHeroImages(updatedImages);
  };

  const updateHeroImageTitle = (index, title) => {
    const updatedImages = [...heroImages];
    updatedImages[index] = { ...updatedImages[index], title };
    setHeroImages(updatedImages);
    saveHeroImages(updatedImages);
  };

  const handleHeroFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadHeroImage(file);
    }
    e.target.value = '';
  };

  // ============================================================
  // 4. FONCTIONS DES 4 BLOCS PUBLICITAIRES HORIZONTAUX
  //    (Situés sous la zone hero)
  // ============================================================
  const fetchAdStrips = async () => {
    setAdStripsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/ad-strips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const strips = Array.isArray(response.data?.strips) ? response.data.strips : [];
      setAdStrips(defaultAdStrips.map((fallback) => ({
        ...fallback,
        ...(strips.find((strip) => strip.id === fallback.id) || {}),
      })));
    } catch (error) {
      console.error('Erreur chargement zones pub:', error);
      toast.error('Impossible de charger les zones publicitaires');
    } finally {
      setAdStripsLoading(false);
    }
  };

  const saveAdStrips = async (nextStrips = adStrips) => {
    setAdStripsSaving(true);
    try {
      await axios.put(`${API}/admin/settings/ad-strips`, { strips: nextStrips }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zones publicitaires sauvegardées !');
    } catch (error) {
      console.error('Erreur sauvegarde zones pub:', error);
      toast.error('Erreur lors de la sauvegarde des zones pub');
    } finally {
      setAdStripsSaving(false);
    }
  };

  const updateAdStrip = (stripId, patch) => {
    setAdStrips(prev => prev.map(strip => strip.id === stripId ? { ...strip, ...patch } : strip));
  };

  const uploadAdStripMedia = async (stripId, file) => {
    if (!file) return;

    const allowedTypes = [
      'image/gif', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp',
      'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Images: GIF, WEBP, PNG, JPEG, JPG. Vidéos: MP4, WEBM, OGG, MOV');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setAdStripUploading(stripId);
    try {
      const response = await axios.post(`${API}/admin/upload/ad-strip-media`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      updateAdStrip(stripId, {
        media_url: response.data.url,
        media_type: response.data.media_type || (file.type.startsWith('video/') ? 'video' : 'image'),
      });
      toast.success('Média publicitaire uploadé !');
    } catch (error) {
      console.error('Erreur upload média pub:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setAdStripUploading(null);
    }
  };

  const handleAdStripMediaChange = (stripId, e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAdStripMedia(stripId, file);
    }
    e.target.value = '';
  };

  // ============================================================
  // 5. FONCTIONS DU BLOC PUBLICITAIRE DROITE - HAUT
  //    (Colonne droite, bloc supérieur - à côté du diaporama)
  // ============================================================
  const fetchRightBlockTopSettings = async () => {
    setRightBlockTopLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/right-block-top`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setRightBlockTopType(data.type_content || 'image');
      setRightBlockTopImage(data.image || '');
      setRightBlockTopVideo(data.video || '');
      setRightBlockTopTitle(data.title || 'Espace publicitaire');
      setRightBlockTopLink(data.link || '');
    } catch (error) {
      console.error('Erreur chargement bloc droit haut:', error);
    } finally {
      setRightBlockTopLoading(false);
    }
  };

  const saveRightBlockTopSettings = async () => {
    setRightBlockTopSaving(true);
    try {
      await axios.put(`${API}/admin/settings/right-block-top`, {
        type_content: rightBlockTopType,
        image: rightBlockTopImage,
        video: rightBlockTopVideo,
        title: rightBlockTopTitle,
        link: rightBlockTopLink
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bloc publicitaire HAUT sauvegardé !');
    } catch (error) {
      console.error('Erreur sauvegarde bloc droit haut:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setRightBlockTopSaving(false);
    }
  };

  const uploadRightBlockTopImage = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez GIF, PNG, JPEG, JPG ou WEBP');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setRightBlockTopUploading(true);
    try {
      const response = await axios.post(`${API}/admin/upload/right-block-top-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      const newImageUrl = response.data.url;
      setRightBlockTopImage(newImageUrl);
      toast.success('Image uploadée pour le bloc HAUT !');
    } catch (error) {
      console.error('Erreur upload image bloc droit haut:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setRightBlockTopUploading(false);
    }
  };

  const handleRightBlockTopImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadRightBlockTopImage(file);
    }
    e.target.value = '';
  };

  // ============================================================
  // 6. FONCTIONS DU BLOC PUBLICITAIRE DROITE - BAS
  //    (Colonne droite, bloc inférieur - sous le bloc haut)
  // ============================================================
  const fetchRightBlockBottomSettings = async () => {
    setRightBlockBottomLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/right-block-bottom`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setRightBlockBottomType(data.type_content || 'image');
      setRightBlockBottomImage(data.image || '');
      setRightBlockBottomVideo(data.video || '');
      setRightBlockBottomTitle(data.title || 'Espace publicitaire');
      setRightBlockBottomLink(data.link || '');
    } catch (error) {
      console.error('Erreur chargement bloc droit bas:', error);
    } finally {
      setRightBlockBottomLoading(false);
    }
  };

  const saveRightBlockBottomSettings = async () => {
    setRightBlockBottomSaving(true);
    try {
      await axios.put(`${API}/admin/settings/right-block-bottom`, {
        type_content: rightBlockBottomType,
        image: rightBlockBottomImage,
        video: rightBlockBottomVideo,
        title: rightBlockBottomTitle,
        link: rightBlockBottomLink
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bloc publicitaire BAS sauvegardé !');
    } catch (error) {
      console.error('Erreur sauvegarde bloc droit bas:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setRightBlockBottomSaving(false);
    }
  };

  const uploadRightBlockBottomImage = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez GIF, PNG, JPEG, JPG ou WEBP');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setRightBlockBottomUploading(true);
    try {
      const response = await axios.post(`${API}/admin/upload/right-block-bottom-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      const newImageUrl = response.data.url;
      setRightBlockBottomImage(newImageUrl);
      toast.success('Image uploadée pour le bloc BAS !');
    } catch (error) {
      console.error('Erreur upload image bloc droit bas:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setRightBlockBottomUploading(false);
    }
  };

  const handleRightBlockBottomImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadRightBlockBottomImage(file);
    }
    e.target.value = '';
  };

  // ============================================================
  // 7. FONCTIONS DU BLOC TENDANCES DU MOMENT
  // ============================================================
  const fetchTrendingSettings = async () => {
    setTrendingLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/trending-block`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setTrendingGradientFrom(data.gradient_from || '#1e293b');
      setTrendingGradientTo(data.gradient_to || '#0f172a');
      setTrendingBackgroundImage(data.background_image || '');
      setTrendingEnableBlurs(data.enable_blurs !== false);
    } catch (error) {
      console.error('Erreur chargement paramètres trending:', error);
      toast.error('Impossible de charger les paramètres du bloc Trending');
    } finally {
      setTrendingLoading(false);
    }
  };

  const saveTrendingSettings = async () => {
    setTrendingSaving(true);
    try {
      await axios.put(`${API}/admin/settings/trending-block`, {
        gradient_from: trendingGradientFrom,
        gradient_to: trendingGradientTo,
        background_image: trendingBackgroundImage,
        enable_blurs: trendingEnableBlurs,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bloc Trending sauvegardé !');
    } catch (error) {
      console.error('Erreur sauvegarde trending:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setTrendingSaving(false);
    }
  };

  const uploadTrendingBackgroundImage = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez GIF, PNG, JPEG, JPG ou WEBP');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setTrendingUploading(true);
    try {
      const response = await axios.post(`${API}/admin/upload/trending-bg`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      const newImageUrl = response.data.url;
      setTrendingBackgroundImage(newImageUrl);
      toast.success('Image de fond uploadée !');
    } catch (error) {
      console.error('Erreur upload image trending:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setTrendingUploading(false);
    }
  };

  const handleTrendingImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadTrendingBackgroundImage(file);
    }
    e.target.value = '';
  };

  // ============================================================
  // 8. FONCTIONS DU LOGO DU SITE
  // ============================================================
  const fetchLogoSettings = async () => {
    setLogoLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogoUrl(response.data?.logo_url || '');
    } catch (error) {
      console.error('Erreur chargement logo:', error);
    } finally {
      setLogoLoading(false);
    }
  };

  const saveLogoSettings = async () => {
    setLogoSaving(true);
    try {
      await axios.put(`${API}/admin/settings/logo`, { logo_url: logoUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Logo sauvegardé !');
    } catch (error) {
      console.error('Erreur sauvegarde logo:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLogoSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez GIF, PNG, JPEG, JPG ou WEBP');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setLogoUploading(true);
    try {
      const response = await axios.post(`${API}/admin/upload/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      const newImageUrl = response.data.url;
      setLogoUrl(newImageUrl);
      toast.success('Logo uploadé !');
    } catch (error) {
      console.error('Erreur upload logo:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadLogo(file);
    }
    e.target.value = '';
  };

  // ============================================================
  // 9. FONCTIONS DU FOND DE PAGE DE CONNEXION
  // ============================================================
  const fetchAuthPageSettings = async () => {
    setAuthPageLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/auth-page`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setAuthPageEnabled(data.enabled || false);
      setAuthPageBackgroundType(data.background_type || 'color');
      setAuthPageBgColor(data.background_color || '');
      setAuthPageBgImages(data.background_images || []);
      setAuthPageLayoutType(data.layout_type || 'single');
    } catch (error) {
      console.error('Erreur chargement paramètres page connexion:', error);
    } finally {
      setAuthPageLoading(false);
    }
  };

  const saveAuthPageSettings = async () => {
    setAuthPageSaving(true);
    try {
      await axios.put(`${API}/admin/settings/auth-page`, {
        enabled: authPageEnabled,
        background_type: authPageBackgroundType,
        background_color: authPageBgColor,
        background_images: authPageBgImages,
        layout_type: authPageLayoutType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres de la page de connexion sauvegardés !');
    } catch (error) {
      console.error('Erreur sauvegarde page connexion:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setAuthPageSaving(false);
    }
  };

  const uploadAuthPageBgImage = async (file, position = null) => {
    if (!file) return;
    
    if (!(file.type || '').startsWith('image/')) {
      toast.error('Sélectionnez un fichier image valide');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setAuthPageUploading(true);
    try {
      const response = await axios.post(`${API}/admin/upload/auth-page-bg`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'multipart/form-data' 
        }
      });
      const newImageUrl = response.data.url;
      
      // Always keep a stable left/right pair. Sparse arrays were serialized as
      // null and caused the second image to disappear from the preview.
      const targetPosition = position === 0 || position === 1
        ? position
        : (authPageBgImages[0] ? 1 : 0);
      setAuthPageBgImages((currentImages) => {
        const updated = [currentImages[0] || '', currentImages[1] || ''];
        updated[targetPosition] = newImageUrl;
        return updated[1] ? updated : updated.slice(0, 1);
      });
      setAuthPageLayoutType(targetPosition === 1 || authPageBgImages[1] ? 'split' : 'single');
      toast.success('Image de fond uploadée !');
    } catch (error) {
      console.error('Erreur upload image fond:', error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setAuthPageUploading(false);
    }
  };

  const handleAuthPageBgImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAuthPageBgImage(file);
    }
    e.target.value = '';
  };

  const updateAuthPageBgImage = (index, value) => {
    setAuthPageBgImages((currentImages) => {
      const updated = [currentImages[0] || '', currentImages[1] || ''];
      updated[index] = value;
      return updated[1] ? updated : updated.slice(0, 1);
    });
    if (index === 1 && value) setAuthPageLayoutType('split');
  };

  const removeAuthPageBgImage = (index) => {
    // If the left image is removed, promote the right image to keep a valid
    // one-image configuration instead of saving an empty first slot.
    const updatedImages = index === 0 && authPageBgImages[1]
      ? [authPageBgImages[1]]
      : authPageBgImages.filter((_, idx) => idx !== index).filter(Boolean);
    setAuthPageBgImages(updatedImages);
    if (updatedImages.length <= 1) {
      setAuthPageLayoutType('single');
    }
  };

  // ============================================================
  // 9. FONCTIONS UTILITAIRES
  // ============================================================
  const getImageUrl = (img) => {
    if (!img) return '';
    if (typeof img === 'object') img = img.url || '';
    if (img.startsWith('/')) return `${API_BASE}${img}`;
    return img;
  };

  const getToneColor = (tone) => {
    switch (tone) {
      case 'orange': return 'from-orange-500 to-amber-500';
      case 'blue': return 'from-blue-500 to-cyan-500';
      case 'green': return 'from-green-500 to-emerald-500';
      case 'red': return 'from-red-500 to-rose-500';
      default: return 'from-orange-500 to-amber-500';
    }
  };

  // ============================================================
  // 10. CHARGEMENT INITIAL DES DONNÉES
  // ============================================================
  React.useEffect(() => {
    fetchHeroImages();
    fetchAdStrips();
    fetchRightBlockTopSettings();
    fetchRightBlockBottomSettings();
    fetchTrendingSettings();
    fetchLogoSettings();
    fetchAuthPageSettings();
  }, [token, API]);

  // ============================================================
  // 11. RENDU DE L'INTERFACE ADMIN
  // ============================================================
  return (
    <div className="space-y-6 p-6">
      
      // =========================================================
      // SECTION 1 : CARROUSEL HERO (DIAPORAMA PRINCIPAL)
      // =========================================================
      <div>
        <h2 className="text-xl font-bold text-slate-100">🎠 Carrousel Hero (Diaporama principal)</h2>
        <p className="text-slate-400 text-sm mt-1">
          Gérez les images qui défileront en haut de la page d'accueil (zone principale).
        </p>
      </div>

      <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-5 border border-purple-500/30">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-purple-300">
          <Image className="w-5 h-5" /> Images du diaporama
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Ajoutez des images (GIF, PNG, JPEG, JPG). Glissez-déposez pour les réordonner. 
          Cliquez sur une image pour modifier son lien et son titre.
        </p>

        <div className="mb-5 p-4 border-2 border-dashed border-purple-500/40 rounded-lg text-center bg-slate-800/30">
          <input
            type="file"
            accept="image/gif,image/jpeg,image/png,image/jpg"
            onChange={handleHeroFileChange}
            disabled={heroUploading}
            id="hero-upload-input"
            className="hidden"
          />
          <label
            htmlFor="hero-upload-input"
            className="inline-flex items-center gap-2 cursor-pointer bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-lg transition"
          >
            <Upload className="w-4 h-4" />
            {heroUploading ? 'Upload en cours...' : '+ Ajouter une image (GIF, PNG, JPEG, JPG)'}
          </label>
          <p className="text-xs text-slate-500 mt-2">Glissez les images pour les réordonner</p>
        </div>

        {heroLoading ? (
          <p className="text-slate-400 text-center py-4">Chargement...</p>
        ) : heroImages.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-800/30 rounded-lg">
            <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Aucune image pour le moment. Ajoutez-en une !</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {heroImages.map((item, idx) => {
              const imageUrl = typeof item === 'string' ? item : item.url;
              const imageLink = typeof item === 'string' ? '' : (item.link || '');
              const imageTitle = typeof item === 'string' ? '' : (item.title || '');
              return (
                <div
                  key={idx}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  className={`relative group rounded-lg overflow-hidden border-2 transition-all bg-slate-800 ${
                    dragOverIndex === idx ? 'border-purple-400 scale-[1.02] shadow-lg shadow-purple-500/30' : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <img
                    src={getImageUrl(imageUrl)}
                    alt={imageTitle || `Hero ${idx + 1}`}
                    className="w-full h-32 object-cover bg-slate-900"
                  />
                  
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity p-3 flex flex-col justify-between">
                    <div>
                      <label className="text-[10px] text-white/70 block mb-1">Lien de redirection</label>
                      <input
                        type="text"
                        value={imageLink}
                        onChange={(e) => updateHeroImageLink(idx, e.target.value)}
                        placeholder="https://..."
                        className="w-full px-2 py-1 text-xs rounded bg-slate-700 text-white border border-slate-500 focus:border-purple-500 outline-none"
                      />
                    </div>
                    <div className="mt-2">
                      <label className="text-[10px] text-white/70 block mb-1">Titre (alt)</label>
                      <input
                        type="text"
                        value={imageTitle}
                        onChange={(e) => updateHeroImageTitle(idx, e.target.value)}
                        placeholder="Titre de l'image"
                        className="w-full px-2 py-1 text-xs rounded bg-slate-700 text-white border border-slate-500 focus:border-purple-500 outline-none"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={() => removeHeroImage(idx)}
                    className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    title="Supprimer"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  
                  <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                    {idx + 1}
                  </div>
                  
                  {imageLink && (
                    <div className="absolute bottom-1 left-1 bg-purple-600/80 text-white text-[9px] px-1.5 py-0.5 rounded-full">
                      🔗 Lié
                    </div>
                  )}
                  
                  <div className="absolute bottom-1 right-1 text-white/40 text-xs">
                    <GripVertical className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        onClick={fetchHeroImages}
        disabled={heroLoading}
        className="w-full py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium text-sm transition disabled:opacity-50"
      >
        <RefreshCw className={`w-4 h-4 inline mr-2 ${heroLoading ? 'animate-spin' : ''}`} />
        Rafraîchir les images
      </button>

      // =========================================================
      // SECTION 2 : BLOCS PUBLICITAIRES DE LA COLONNE DROITE
      //            (Bloc HAUT et Bloc BAS - à côté du diaporama)
      // =========================================================
      
      // ---------------------------------------------------------
      // 2.1 BLOC PUBLICITAIRE HAUT (Colonne droite)
      // ---------------------------------------------------------
      <div className="mt-8">
        <h2 className="text-xl font-bold text-slate-100">📺 Bloc publicitaire - Colonne droite (HAUT)</h2>
        <p className="text-slate-400 text-sm mt-1">
          Configurez l'image ou la vidéo qui apparaîtra en HAUT de la colonne droite, à côté du diaporama.
        </p>
      </div>

      <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 rounded-xl p-5 border border-emerald-500/30">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-emerald-300">
          <Image className="w-5 h-5" /> Bloc HAUT
        </h3>

        {rightBlockTopLoading ? (
          <p className="text-slate-400 text-center py-4">Chargement...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 block mb-2">Type de contenu</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="right_block_top_type" value="image" checked={rightBlockTopType === 'image'} onChange={() => setRightBlockTopType('image')} />
                  <span className="text-slate-200">🖼️ Image</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="right_block_top_type" value="video" checked={rightBlockTopType === 'video'} onChange={() => setRightBlockTopType('video')} />
                  <span className="text-slate-200">🎬 Vidéo YouTube</span>
                </label>
              </div>
            </div>

            {rightBlockTopType === 'image' && (
              <div>
                <label className="text-sm text-slate-300 block mb-2">Image publicitaire</label>
                <div className="flex gap-4 items-start flex-wrap">
                  {rightBlockTopImage && <img src={getImageUrl(rightBlockTopImage)} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-[200px]">
                    <input type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleRightBlockTopImageChange} disabled={rightBlockTopUploading} className="hidden" id="right-block-top-upload" />
                    <label htmlFor="right-block-top-upload" className="inline-flex items-center gap-2 cursor-pointer bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition"><Upload className="w-4 h-4" />{rightBlockTopUploading ? 'Upload en cours...' : '📁 Choisir une image'}</label>
                    {rightBlockTopImage && <button onClick={() => setRightBlockTopImage('')} className="ml-2 text-red-400 hover:text-red-300 text-sm">Supprimer</button>}
                    <input type="text" value={rightBlockTopImage} onChange={(e) => setRightBlockTopImage(e.target.value)} placeholder="Ou URL de l'image" className="mt-2 w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
                  </div>
                </div>
              </div>
            )}

            {rightBlockTopType === 'video' && (
              <div>
                <label className="text-sm text-slate-300 block mb-2">URL YouTube</label>
                <input type="text" value={rightBlockTopVideo} onChange={(e) => setRightBlockTopVideo(e.target.value)} placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..." className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
                {rightBlockTopVideo && (
                  <div className="mt-3 aspect-video bg-slate-800 rounded-lg overflow-hidden">
                    <iframe src={rightBlockTopVideo.includes('youtube.com/embed') ? rightBlockTopVideo : rightBlockTopVideo.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} className="w-full h-full" frameBorder="0" allowFullScreen />
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-sm text-slate-300 block mb-2">Titre (optionnel)</label>
              <input type="text" value={rightBlockTopTitle} onChange={(e) => setRightBlockTopTitle(e.target.value)} placeholder="Espace publicitaire" className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
            </div>

            <div>
              <label className="text-sm text-slate-300 block mb-2">Lien de redirection (optionnel)</label>
              <input type="text" value={rightBlockTopLink} onChange={(e) => setRightBlockTopLink(e.target.value)} placeholder="https://... ou /produits" className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
            </div>

            <button onClick={saveRightBlockTopSettings} disabled={rightBlockTopSaving} className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition disabled:opacity-50">
              {rightBlockTopSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder le bloc HAUT'}
            </button>
          </div>
        )}
      </div>

      // ---------------------------------------------------------
      // 2.2 BLOC PUBLICITAIRE BAS (Colonne droite)
      // ---------------------------------------------------------
      <div className="mt-8">
        <h2 className="text-xl font-bold text-slate-100">📺 Bloc publicitaire - Colonne droite (BAS)</h2>
        <p className="text-slate-400 text-sm mt-1">
          Configurez l'image ou la vidéo qui apparaîtra en BAS de la colonne droite, sous le bloc haut.
        </p>
      </div>

      <div className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 rounded-xl p-5 border border-blue-500/30">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-blue-300">
          <Image className="w-5 h-5" /> Bloc BAS
        </h3>

        {rightBlockBottomLoading ? (
          <p className="text-slate-400 text-center py-4">Chargement...</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 block mb-2">Type de contenu</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="right_block_bottom_type" value="image" checked={rightBlockBottomType === 'image'} onChange={() => setRightBlockBottomType('image')} />
                  <span className="text-slate-200">🖼️ Image</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="right_block_bottom_type" value="video" checked={rightBlockBottomType === 'video'} onChange={() => setRightBlockBottomType('video')} />
                  <span className="text-slate-200">🎬 Vidéo YouTube</span>
                </label>
              </div>
            </div>

            {rightBlockBottomType === 'image' && (
              <div>
                <label className="text-sm text-slate-300 block mb-2">Image publicitaire</label>
                <div className="flex gap-4 items-start flex-wrap">
                  {rightBlockBottomImage && <img src={getImageUrl(rightBlockBottomImage)} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-[200px]">
                    <input type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleRightBlockBottomImageChange} disabled={rightBlockBottomUploading} className="hidden" id="right-block-bottom-upload" />
                    <label htmlFor="right-block-bottom-upload" className="inline-flex items-center gap-2 cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"><Upload className="w-4 h-4" />{rightBlockBottomUploading ? 'Upload en cours...' : '📁 Choisir une image'}</label>
                    {rightBlockBottomImage && <button onClick={() => setRightBlockBottomImage('')} className="ml-2 text-red-400 hover:text-red-300 text-sm">Supprimer</button>}
                    <input type="text" value={rightBlockBottomImage} onChange={(e) => setRightBlockBottomImage(e.target.value)} placeholder="Ou URL de l'image" className="mt-2 w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
                  </div>
                </div>
              </div>
            )}

            {rightBlockBottomType === 'video' && (
              <div>
                <label className="text-sm text-slate-300 block mb-2">URL YouTube</label>
                <input type="text" value={rightBlockBottomVideo} onChange={(e) => setRightBlockBottomVideo(e.target.value)} placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..." className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
                {rightBlockBottomVideo && (
                  <div className="mt-3 aspect-video bg-slate-800 rounded-lg overflow-hidden">
                    <iframe src={rightBlockBottomVideo.includes('youtube.com/embed') ? rightBlockBottomVideo : rightBlockBottomVideo.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} className="w-full h-full" frameBorder="0" allowFullScreen />
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-sm text-slate-300 block mb-2">Titre (optionnel)</label>
              <input type="text" value={rightBlockBottomTitle} onChange={(e) => setRightBlockBottomTitle(e.target.value)} placeholder="Espace publicitaire" className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
            </div>

            <div>
              <label className="text-sm text-slate-300 block mb-2">Lien de redirection (optionnel)</label>
              <input type="text" value={rightBlockBottomLink} onChange={(e) => setRightBlockBottomLink(e.target.value)} placeholder="https://... ou /produits" className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
            </div>

            <button onClick={saveRightBlockBottomSettings} disabled={rightBlockBottomSaving} className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition disabled:opacity-50">
              {rightBlockBottomSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder le bloc BAS'}
            </button>
          </div>
        )}
      </div>

      // =========================================================
      // SECTION 3 : ZONES PUBLICITAIRES HORIZONTALES (4 BLOCS)
      //            (Situés sous la zone hero)
      // =========================================================
      <div className="mt-8">
        <h2 className="text-xl font-bold text-slate-100">📢 Zones publicitaires horizontales (sous la hero)</h2>
        <p className="text-slate-400 text-sm mt-1">
          Configurez les 4 espaces publicitaires horizontaux qui apparaissent sous la zone hero.
          Formats acceptés : GIF, WEBP, PNG, JPEG, JPG (images) / MP4, WEBM, OGG, MOV (vidéos).
        </p>
      </div>

      <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-xl p-5 border border-cyan-500/30">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-cyan-300">
          <Image className="w-5 h-5" /> 4 espaces publicitaires horizontaux
        </h3>

        {adStripsLoading ? (
          <p className="text-slate-400 text-center py-4">Chargement...</p>
        ) : (
          <div className="space-y-5">
            {adStrips.map((strip) => (
              <div key={strip.id} className="rounded-xl border border-slate-700 bg-slate-800/50 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h4 className="font-bold text-slate-100">{strip.label || strip.title}</h4>
                    <p className="text-xs text-slate-400">ID : {strip.id}</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-200">
                    <input
                      type="checkbox"
                      checked={strip.enabled !== false}
                      onChange={(e) => updateAdStrip(strip.id, { enabled: e.target.checked })}
                    />
                    Activer cette zone
                  </label>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-[220px,1fr]">
                  <div className="rounded-lg overflow-hidden bg-slate-900 border border-slate-700 min-h-[140px] flex items-center justify-center">
                    {strip.media_type === 'video' && strip.media_url ? (
                      <video src={getImageUrl(strip.media_url)} className="w-full h-40 object-cover" controls />
                    ) : strip.media_type === 'image' && strip.media_url ? (
                      <img src={getImageUrl(strip.media_url)} alt={strip.title} className="w-full h-40 object-cover" />
                    ) : (
                      <div className={`w-full h-40 flex items-center justify-center bg-gradient-to-r ${getToneColor(strip.tone)} opacity-70`}>
                        <span className="text-white text-sm font-bold px-3 py-1 bg-black/30 rounded-full">{strip.title}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Titre</label>
                        <input
                          type="text"
                          value={strip.title}
                          onChange={(e) => updateAdStrip(strip.id, { title: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Lien de redirection</label>
                        <input
                          type="text"
                          value={strip.link || ''}
                          onChange={(e) => updateAdStrip(strip.id, { link: e.target.value })}
                          placeholder="/produits ou https://..."
                          className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-400 block mb-1">Sous-titre</label>
                      <input
                        type="text"
                        value={strip.subtitle || ''}
                        onChange={(e) => updateAdStrip(strip.id, { subtitle: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                      />
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Style couleur</label>
                        <select
                          value={strip.tone || 'orange'}
                          onChange={(e) => updateAdStrip(strip.id, { tone: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                        >
                          <option value="orange">Orange</option>
                          <option value="blue">Bleu</option>
                          <option value="green">Vert</option>
                          <option value="red">Rouge</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Type média</label>
                        <select
                          value={strip.media_type || 'none'}
                          onChange={(e) => updateAdStrip(strip.id, { media_type: e.target.value, media_url: e.target.value === 'none' ? '' : strip.media_url })}
                          className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                        >
                          <option value="none">Aucun</option>
                          <option value="image">Image / GIF</option>
                          <option value="video">Vidéo uploadée</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-400 block mb-1">Upload média</label>
                        <input
                          type="file"
                          accept="image/gif,image/webp,image/png,image/jpeg,image/jpg,video/mp4,video/webm,video/ogg,video/quicktime"
                          onChange={(e) => handleAdStripMediaChange(strip.id, e)}
                          disabled={adStripUploading === strip.id}
                          id={`ad-strip-upload-${strip.id}`}
                          className="hidden"
                        />
                        <label htmlFor={`ad-strip-upload-${strip.id}`} className="inline-flex w-full items-center justify-center gap-2 cursor-pointer bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-2 rounded-lg transition text-sm">
                          <Upload className="w-4 h-4" />
                          {adStripUploading === strip.id ? 'Upload...' : 'Choisir'}
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-slate-400 block mb-1">URL média</label>
                      <input
                        type="text"
                        value={strip.media_url || ''}
                        onChange={(e) => updateAdStrip(strip.id, { media_url: e.target.value })}
                        placeholder="/uploads/..."
                        className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={() => saveAdStrips()}
              disabled={adStripsSaving}
              className="w-full py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-medium transition disabled:opacity-50"
            >
              {adStripsSaving ? 'Sauvegarde...' : '💾 Sauvegarder les 4 zones publicitaires'}
            </button>
          </div>
        )}
      </div>

      // =========================================================
      // SECTION 4 : LOGO DU SITE
      // =========================================================
      <div className="mt-8">
        <h2 className="text-xl font-bold text-slate-100">🏷️ Logo du site</h2>
        <p className="text-slate-400 text-sm mt-1">
          Gérez le logo qui apparaît dans la barre de navigation. Formats acceptés : GIF, PNG, JPEG, JPG, WEBP (max 2 Mo).
        </p>
      </div>

      <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-xl p-5 border border-amber-500/30">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-amber-300">
          <Image className="w-5 h-5" /> Logo
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-center p-4 bg-slate-800/50 rounded-lg">
            {logoLoading ? (
              <div className="w-32 h-12 bg-slate-700 animate-pulse rounded"></div>
            ) : logoUrl ? (
              <img src={getImageUrl(logoUrl)} alt="Logo du site" className="h-12 w-auto object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-xl">C</div>
                <span className="text-2xl font-bold"><span className="text-orange-500">Clo</span><span className="text-amber-600">léo</span></span>
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-slate-300 block mb-2">Logo (remplace le texte "Cloléo")</label>
            <div className="flex gap-4 items-start flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <input type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" onChange={handleLogoChange} disabled={logoUploading} className="hidden" id="logo-upload" />
                <label htmlFor="logo-upload" className="inline-flex items-center gap-2 cursor-pointer bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg transition"><Upload className="w-4 h-4" />{logoUploading ? 'Upload en cours...' : '📁 Choisir un logo'}</label>
                {logoUrl && <button onClick={() => setLogoUrl('')} className="ml-2 text-red-400 hover:text-red-300 text-sm">Supprimer</button>}
                <input type="text" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="Ou URL du logo" className="mt-2 w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm" />
                <p className="text-xs text-slate-500 mt-2">Le logo sera affiché dans la barre de navigation à la place du texte "Cloléo".</p>
              </div>
            </div>
          </div>

          <button onClick={saveLogoSettings} disabled={logoSaving} className="w-full py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium transition disabled:opacity-50">
            {logoSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder le logo'}
          </button>
        </div>
      </div>

      // =========================================================
      // SECTION 5 : BLOC TENDANCES DU MOMENT
      // =========================================================
      <div className="mt-8">
        <h2 className="text-xl font-bold text-slate-100">🔥 Bloc Tendance du moment</h2>
        <p className="text-slate-400 text-sm mt-1">
          Personnalisez l'apparence du bloc qui affiche les produits tendances. 
          Changez les couleurs du dégradé, ajoutez une image de fond, et gérez les effets visuels.
        </p>
      </div>

      <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-xl p-5 border border-purple-500/30">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-purple-300">
          <Palette className="w-5 h-5" /> Personnalisation du bloc
        </h3>

        {trendingLoading ? (
          <p className="text-slate-400 text-center py-4">Chargement...</p>
        ) : (
          <div className="space-y-5">
            <div>
              <label className="text-sm text-slate-300 block mb-2">Aperçu</label>
              <div
                className="h-32 rounded-lg border border-slate-600 overflow-hidden shadow-lg"
                style={{
                  background: trendingBackgroundImage
                    ? `url(${trendingBackgroundImage}) center/cover`
                    : `linear-gradient(135deg, ${trendingGradientFrom}, ${trendingGradientTo})`,
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-300 block mb-2">Couleur de départ</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={trendingGradientFrom}
                    onChange={(e) => setTrendingGradientFrom(e.target.value)}
                    className="w-12 h-10 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={trendingGradientFrom}
                    onChange={(e) => setTrendingGradientFrom(e.target.value)}
                    placeholder="#000000"
                    className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-300 block mb-2">Couleur d'arrivée</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={trendingGradientTo}
                    onChange={(e) => setTrendingGradientTo(e.target.value)}
                    className="w-12 h-10 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={trendingGradientTo}
                    onChange={(e) => setTrendingGradientTo(e.target.value)}
                    placeholder="#000000"
                    className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-300 block mb-2">Image de fond (optionnel)</label>
              <div className="flex gap-4 items-start flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleTrendingImageChange}
                    disabled={trendingUploading}
                    className="hidden"
                    id="trending-bg-upload"
                  />
                  <label
                    htmlFor="trending-bg-upload"
                    className="inline-flex items-center gap-2 cursor-pointer bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition"
                  >
                    <Upload className="w-4 h-4" />
                    {trendingUploading ? 'Upload en cours...' : '📁 Choisir une image'}
                  </label>
                  {trendingBackgroundImage && (
                    <button
                      onClick={() => setTrendingBackgroundImage('')}
                      className="ml-2 text-red-400 hover:text-red-300 text-sm"
                    >
                      Supprimer l'image
                    </button>
                  )}
                  <input
                    type="text"
                    value={trendingBackgroundImage}
                    onChange={(e) => setTrendingBackgroundImage(e.target.value)}
                    placeholder="Ou URL de l'image"
                    className="mt-2 w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-2">Si vous ajoutez une image, elle s'affichera par-dessus le gradient.</p>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-700">
              <label className="flex items-center gap-2 cursor-pointer text-slate-200">
                <input
                  type="checkbox"
                  checked={trendingEnableBlurs}
                  onChange={(e) => setTrendingEnableBlurs(e.target.checked)}
                  className="w-4 h-4 rounded cursor-pointer"
                />
                <span className="text-sm">Afficher les effets de flou animés (bulles flottantes)</span>
              </label>
              <p className="text-xs text-slate-500 mt-2">Désactivez pour une apparence plus épurée.</p>
            </div>

            <button
              onClick={saveTrendingSettings}
              disabled={trendingSaving}
              className="w-full py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium transition disabled:opacity-50"
            >
              {trendingSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les paramètres'}
            </button>
          </div>
        )}
      </div>

      // =========================================================
      // SECTION 6 : FOND DE PAGE DE CONNEXION
      // =========================================================
      <div className="mt-8">
        <h2 className="text-xl font-bold text-slate-100">🔐 Fond de la page de connexion</h2>
        <p className="text-slate-400 text-sm mt-1">
          Configurez le fond de la page de connexion. Activez le fond personnalisé, puis choisissez entre une couleur ou des images.
        </p>
      </div>

      <div className="bg-gradient-to-r from-rose-900/30 to-pink-900/30 rounded-xl p-5 border border-rose-500/30">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-rose-300">
          <Image className="w-5 h-5" /> Configuration du fond
        </h3>

        {authPageLoading ? (
          <p className="text-slate-400 text-center py-4">Chargement...</p>
        ) : (
          <div className="space-y-6">
            {/* Activation du fond */}
            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div>
                <label className="text-sm font-medium text-slate-200 block mb-1">Activer le fond personnalisé</label>
                <p className="text-xs text-slate-400">Désactivez pour utiliser le gradient par défaut</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={authPageEnabled}
                  onChange={(e) => setAuthPageEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-rose-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-600"></div>
              </label>
            </div>

            {authPageEnabled && (
              <>
                {/* Type de fond */}
                <div>
                  <label className="text-sm text-slate-300 block mb-3">Type de fond</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="auth_bg_type"
                        value="color"
                        checked={authPageBackgroundType === 'color'}
                        onChange={() => setAuthPageBackgroundType('color')}
                      />
                      <span className="text-slate-200">Couleur unie</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="auth_bg_type"
                        value="image"
                        checked={authPageBackgroundType === 'image'}
                        onChange={() => setAuthPageBackgroundType('image')}
                      />
                      <span className="text-slate-200">Image(s)</span>
                    </label>
                  </div>
                </div>

                {/* Aperçu */}
                <div>
                  <label className="text-sm text-slate-300 block mb-2">Aperçu</label>
                  <div className="relative h-40 overflow-hidden rounded-lg border border-slate-600 shadow-lg">
                    {authPageBackgroundType === 'color' && (
                      <div className="absolute inset-0" style={{ backgroundColor: authPageBgColor || '#ffffff' }} />
                    )}
                    {authPageBackgroundType === 'image' && authPageBgImages[0] && (
                      authPageLayoutType === 'split' && authPageBgImages[1] ? (
                        <div className="absolute inset-0 flex">
                          <div className="h-full w-1/2 bg-cover bg-center" style={{ backgroundImage: `url("${getImageUrl(authPageBgImages[0])}")` }} />
                          <div className="h-full w-1/2 bg-cover bg-center" style={{ backgroundImage: `url("${getImageUrl(authPageBgImages[1])}")` }} />
                        </div>
                      ) : (
                        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${getImageUrl(authPageBgImages[0])}")` }} />
                      )
                    )}
                    {authPageBackgroundType === 'image' && !authPageBgImages[0] && (
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-400" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/15">
                      <span className="rounded-full bg-white/85 px-3 py-1 text-xs font-medium text-slate-700">Aperçu de la page de connexion</span>
                    </div>
                  </div>
                </div>

                {/* Configuration couleur */}
                {authPageBackgroundType === 'color' && (
                  <div>
                    <label className="text-sm text-slate-300 block mb-2">Couleur de fond</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={authPageBgColor || '#ffffff'}
                        onChange={(e) => setAuthPageBgColor(e.target.value)}
                        className="w-12 h-10 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={authPageBgColor}
                        onChange={(e) => setAuthPageBgColor(e.target.value)}
                        placeholder="#ffffff"
                        className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Configuration images */}
                {authPageBackgroundType === 'image' && (
                  <div className="space-y-4">
                    {/* Image de gauche */}
                    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                      <label className="text-sm font-medium text-slate-200 block mb-2">🖼️ Image de gauche</label>
                      {authPageBgImages[0] ? (
                        <div className="space-y-3">
                          <img src={getImageUrl(authPageBgImages[0])} alt="Gauche" className="w-full h-32 object-cover rounded-lg" />
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={authPageBgImages[0]}
                              onChange={(e) => updateAuthPageBgImage(0, e.target.value)}
                              className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                            />
                            <button
                              onClick={() => removeAuthPageBgImage(0)}
                              className="text-red-400 hover:text-red-300 px-3 py-2 rounded bg-red-900/30"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                uploadAuthPageBgImage(file, 0);
                              }
                              e.target.value = '';
                            }}
                            disabled={authPageUploading}
                            className="hidden"
                            id="auth-page-bg-left"
                          />
                          <label
                            htmlFor="auth-page-bg-left"
                            className="inline-flex items-center gap-2 cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
                          >
                            <Upload className="w-4 h-4" />
                            {authPageUploading ? 'Upload en cours...' : '📁 Choisir l\'image de gauche'}
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Image de droite */}
                    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                      <label className="text-sm font-medium text-slate-200 block mb-2">🖼️ Image de droite</label>
                      {authPageBgImages[1] ? (
                        <div className="space-y-3">
                          <img src={getImageUrl(authPageBgImages[1])} alt="Droite" className="w-full h-32 object-cover rounded-lg" />
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={authPageBgImages[1]}
                              onChange={(e) => updateAuthPageBgImage(1, e.target.value)}
                              className="flex-1 px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm"
                            />
                            <button
                              onClick={() => removeAuthPageBgImage(1)}
                              className="text-red-400 hover:text-red-300 px-3 py-2 rounded bg-red-900/30"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                uploadAuthPageBgImage(file, 1);
                              }
                              e.target.value = '';
                            }}
                            disabled={authPageUploading}
                            className="hidden"
                            id="auth-page-bg-right"
                          />
                          <label
                            htmlFor="auth-page-bg-right"
                            className="inline-flex items-center gap-2 cursor-pointer bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition"
                          >
                            <Upload className="w-4 h-4" />
                            {authPageUploading ? 'Upload en cours...' : '📁 Choisir l\'image de droite'}
                          </label>
                        </div>
                      )}
                    </div>

                    {/* Option pour n'utiliser qu'une seule image */}
                    <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={authPageLayoutType === 'single'}
                          onChange={(e) => setAuthPageLayoutType(e.target.checked ? 'single' : 'split')}
                          className="w-4 h-4 rounded cursor-pointer"
                        />
                        <span className="text-sm text-slate-200">Utiliser uniquement l'image de gauche (plein écran)</span>
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}

            <button
              onClick={saveAuthPageSettings}
              disabled={authPageSaving}
              className="w-full py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-medium transition disabled:opacity-50"
            >
              {authPageSaving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les paramètres'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
export default AdminDashboard;
